import { ReactNode, memo, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormHelperText,
    FormLabel,
    LinearProgress,
    Link,
    ListItemText,
    ListSubheader,
    MenuItem,
    Select,
    Stack,
    TextField,
    TextFieldProps,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { Add, Delete, PictureAsPdf, Refresh, RestartAlt } from '@mui/icons-material';
import { SxProps, Theme, alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { useTranslation } from 'react-i18next';
import { ValidationError } from 'yup';

import BUILDING_FUNCTIONS_MAPPING from 'src/common/buildingFunctions.json';
import { ensanSteckbriefService } from '../ensan-steckbrief.service';
import {
    CreateVariantRequest,
    DEFAULT_SOURCE,
    MeasureKey,
    PrefilledVariantField,
    ProposedMeasureDto,
    PreviewSteckbriefDocumentRequest,
    RecommendedHeatingSystemKey,
    SteckbriefAssumptionDefaultsDto,
    SteckbriefCalculationResultDto,
    SteckbriefDto,
    SteckbriefKind,
    USER_PROVIDED_SOURCE,
    VariantPrefillDto,
} from '../ensan-steckbrief.dto';
import {
    ANSWERED_KEYS_CONTEXT,
    CHARACTER_LIMIT_EXCEEDED_MSG,
    ConsumptionEntry,
    DEFAULT_INVESTMENT_COST_FACTOR_PERCENT,
    ENERGY_PRICE_PROGNOSIS_KEYS,
    ENGINE_ECHO_FIELD_KEYS,
    EXTRA_INFORMATION_MONUMENT_SENTENCE,
    EnergyPriceFieldKey,
    HEAT_PUMP_DISTRICT_HEATING_HINT_KEY,
    HEATING_RENEWAL_AGE_YEARS,
    LOOKALIKE_REFERENCE_KEYS,
    MEASURE_CHIP_LABELS,
    OFFERED_MEASURE_KEYS,
    RENOVATION_PARTIAL_VALUE,
    RETIRED_MEASURE_KEYS,
    STECKBRIEF_BUILDING_FUNCTION_CODES,
    STECKBRIEF_VARIANT_FIELDS,
    SteckbriefVariantField,
    VARIANT_FORM_SECTIONS,
    buildVariantValuesSchema,
    carriedLegacyKeys,
    currentAsOfMonth,
    exceedsMaxLength,
    fieldVisibleIn,
    formatDecimalInput,
    formatGroupedDecimalInput,
    heatingRenewalSentence,
    isEnergyPriceField,
    isEngineEchoField,
    isEnteredEnergyPrice,
    measureLabel,
    missingCityGmlFieldKeys,
    parseDecimalInput,
    parseGroupedDecimalInput,
    normalizeVariantPrefill,
    seedHeatingYearGate,
    unavailableMeasures,
    variantFieldByKey,
} from '../steckbrief-variant-fields';
import {
    assumptionDefaultSource,
    followAssumptionDefaults,
    isAssumptionDefaultedField,
    resolveAssumptionDefault,
    seedAssumptionDefaults,
} from '../steckbrief-assumption-defaults';
import { formatAssumptionNumber, measureRationale, prognosisWarningSummary } from '../steckbrief-calculation-messages';
import { INVESTMENT_NOT_AVAILABLE_KEY, formatEuroRange } from '../steckbrief-investment';
import { SteckbriefAssumptionsSection } from './SteckbriefAssumptionsSection';
import { SteckbriefLookalikePanel } from './SteckbriefLookalikePanel';
import { SteckbriefPrognosisPanel } from './SteckbriefPrognosisPanel';
import { SteckbriefPrognosisWarnings } from './SteckbriefPrognosisWarnings';
import { InlineSteckbriefPreview, SteckbriefPdfPreviewPanel } from './SteckbriefPdfPreviewPanel';

/**
 * Form-state marker of a half-typed/invalid Stand month (GEN-786) — fails the
 * YYYY-MM schema, so the save is blocked with the inline error.
 */
const INVALID_MONTH = 'invalid';

/** Toggle value of the "unknown" button of a button select (GEN-801) — it writes null. */
const UNKNOWN_BUTTON_VALUE = '__unknown__';

/**
 * The four `/calculate` inputs — the drift check compares the current ones against the displayed result's.
 * The Variant kind (GEN-806) travels on the request too but is no drift input: nothing a save stamps
 * depends on it, so a kind switch recalculates at once instead of outdating the result.
 */
interface CalcInputs {
    values: Record<string, unknown>;
    selectedMeasures: MeasureKey[] | null;
    heatingOverride: RecommendedHeatingSystemKey | null;
    priorityOverride: MeasureKey[] | null;
}

function sameMeasureSet(a: MeasureKey[] | null, b: MeasureKey[] | null): boolean {
    if (a === null || b === null) return a === b;
    if (a.length !== b.length) return false;
    const sortedB = [...b].sort();
    return [...a].sort().every((measure, index) => measure === sortedB[index]);
}

/**
 * True while the current inputs still equal those of the displayed result —
 * type-tolerant per value like the source badges, so an edit undone by hand
 * clears the outdated marker again.
 */
function calcInputsEqual(a: CalcInputs, b: CalcInputs): boolean {
    const keys = new Set([...Object.keys(a.values), ...Object.keys(b.values)]);
    for (const key of keys) {
        if (!valuesEqual(a.values[key], b.values[key])) return false;
    }
    return (
        sameMeasureSet(a.selectedMeasures, b.selectedMeasures) &&
        sameMeasureSet(a.priorityOverride, b.priorityOverride) &&
        a.heatingOverride === b.heatingOverride
    );
}

/**
 * The advisor's classification of one measure — Prio, Empfohlen and Nicht
 * empfohlen are points on ONE scale, so each measure row carries a single
 * three-way switch instead of a selection toggle plus a separate Prio flag.
 * On the wire it maps to the two `/calculate` inputs: selected = every
 * non-NOT_RECOMMENDED row, priority override = the PRIO rows.
 */
type MeasureLevel = 'PRIO' | 'RECOMMENDED' | 'NOT_RECOMMENDED';

/** Scale order of the switch, highest first. */
const MEASURE_LEVELS: MeasureLevel[] = ['PRIO', 'RECOMMENDED', 'NOT_RECOMMENDED'];

/** i18next keys of the level labels (switch, system column, legend). */
const MEASURE_LEVEL_LABEL_KEYS: Record<MeasureLevel, string> = {
    PRIO: 'Prio',
    RECOMMENDED: 'Recommended',
    NOT_RECOMMENDED: 'Not recommended',
};

/**
 * How many measures the advisor may classify per level: 1–5 Prio and 1–6
 * Empfohlen (recommended but not Prio). A switch click that would leave a
 * range is rejected with a message instead of being applied. Only a change
 * that moves a count FURTHER out of range is rejected, so a system proposal
 * already outside a range (e.g. no Prio at all) can still be edited towards it.
 */
const MEASURE_LEVEL_LIMITS: Partial<
    Record<MeasureLevel, { min: number; max: number; minMessageKey: string; maxMessageKey: string }>
> = {
    PRIO: {
        min: 1,
        max: 5,
        minMessageKey: 'At least one measure must be marked as priority.',
        maxMessageKey: 'Max priorities reached: Please, remove at least one priority so you can add a new one.',
    },
    RECOMMENDED: {
        min: 1,
        max: 6,
        minMessageKey: 'At least one measure must be marked as recommended.',
        maxMessageKey:
            'Max recommended measures reached: Please, remove at least one recommended measure so you can add a new one.',
    },
};

function countLevels(rows: { level: MeasureLevel }[]): Record<MeasureLevel, number> {
    const counts: Record<MeasureLevel, number> = { PRIO: 0, RECOMMENDED: 0, NOT_RECOMMENDED: 0 };
    for (const row of rows) counts[row.level] += 1;
    return counts;
}

/** i18next key of the message rejecting a classification change, or null when the change is within the limits. */
function measureLimitViolation(
    current: Record<MeasureLevel, number>,
    next: Record<MeasureLevel, number>,
): string | null {
    for (const level of MEASURE_LEVELS) {
        const limit = MEASURE_LEVEL_LIMITS[level];
        if (!limit) continue;
        if (next[level] > limit.max && next[level] > current[level]) return limit.maxMessageKey;
        if (next[level] < limit.min && next[level] < current[level]) return limit.minMessageKey;
    }
    return null;
}

/** Print-free, internal reason for a changed classification — same budget as the prognosis reasons. */
const MEASURE_REASON_MAX_LENGTH = 500;

/** i18next key of the inline error on a changed row saved without a reason. */
const MEASURE_REASON_REQUIRED_MSG = 'Please give a reason for the change.';

/** Per-measure reason (or inline error) map of the review list. */
type MeasureTextMap = Partial<Record<MeasureKey, string>>;

/** Outcome the page reports back for a save-shaped action (GEN-765): a taken
 * name renders as an INLINE field error, everything else is page-handled. */
export type VariantSaveResult = 'saved' | 'nameTaken' | 'failed';

/** i18next key of the inline name-uniqueness error (client check and 409 map alike). */
const NAME_TAKEN_MSG_KEY = 'This name is already used by an active variant of this Steckbrief.';

/** DOM id of the variant name input — the footer's "Show" link scrolls back to it. */
const VARIANT_NAME_INPUT_ID = 'steckbrief-variant-name';

interface SteckbriefVariantFormProps {
    steckbrief: SteckbriefDto;
    /**
     * Prefill the form seeds from — the `/variants/prefill` response, or the
     * open-mode synthesis from a Variant's data stamp (GEN-765). Read at mount
     * and on every `prefillVersion` bump (the merge re-seed); other prop
     * changes are ignored by the once-seeded form state.
     */
    prefill: VariantPrefillDto;
    /**
     * Bumped by the host whenever `prefill` was RE-RESOLVED (map-subset PATCH,
     * GEN-765). Triggers the merge re-seed: fields still at their previous
     * prefill baseline adopt the new value+source, USER_PROVIDED edits keep the
     * user's value, then exactly one `/calculate` re-run fires on the merge.
     */
    prefillVersion?: number;
    /**
     * The model defaults per Gebäudetyp (GEN-802) the defaulted fields (the
     * window area share) seed from and follow — read once at mount like
     * `prefill`. Null when the page could not load them: those fields then
     * start empty, and the advisor enters them by hand (the share is required
     * on FINAL since GEN-801).
     */
    assumptionDefaults?: SteckbriefAssumptionDefaultsDto | null;
    /**
     * Kind preselected in the kind switch — FINAL unless a caller overrides it
     * (GEN-801: the required rules are FINAL's, so the form starts there).
     */
    initialKind?: SteckbriefKind;
    /**
     * Open-mode measures seeding (GEN-765): the opened Variant's stamped
     * selection/overrides. They seed the panel state AND parametrize the
     * initial `/calculate` run (which is `null,null,null` in create mode).
     */
    initialSelectedMeasures?: MeasureKey[] | null;
    initialHeatingOverride?: RecommendedHeatingSystemKey | null;
    initialPriorityOverride?: MeasureKey[] | null;
    /**
     * Open mode: the opened Variant's stamped reasons for its changed
     * classifications. Seeded once; a row back at the system proposal drops
     * its reason.
     */
    initialMeasureReasons?: MeasureTextMap;
    /**
     * Open mode (GEN-799): the opened Variant's stamped `pvEstimate.estimateId`
     * — the estimate the form displays first, sent on the initial `/calculate`.
     * Null in create mode and for a stamp saved before the feature.
     */
    initialPvEstimateId?: string | null;
    /**
     * Open mode (GEN-765): the source Variant. Its name becomes the name
     * field's PLACEHOLDER (the field seeds empty — saving always creates a new
     * Variant), and a Variant without a generated document gets the
     * "Generate PDF" footer action (rendering from its SAVED stamp).
     */
    openedVariant?: { name: string; documentInstanceId: string | null } | null;
    /** ACTIVE Variant names of this Steckbrief — inline uniqueness check (GEN-763 rule: trimmed, case-insensitive). */
    activeVariantNames?: string[];
    saving: boolean;
    previewing?: boolean;
    generating?: boolean;
    onSave: (request: CreateVariantRequest) => Promise<VariantSaveResult>;
    /** "Speichern & PDF erzeugen" — save, then generate; absent hides the action. */
    onSaveAndGenerate?: (request: CreateVariantRequest) => Promise<VariantSaveResult>;
    /**
     * Inline "Steckbrief-Vorschau": called after every successful calculation
     * (and on a kind switch) with the preview request; absent hides the panel.
     */
    onPreview?: (request: PreviewSteckbriefDocumentRequest) => void;
    /** The last preview render's failure, shown in the panel. */
    previewError?: string | null;
    /** The last rendered preview, shown in the panel; `null` until the first render. */
    preview?: InlineSteckbriefPreview | null;
    /** Download name when the server sends no Content-Disposition file name. */
    previewFileName?: string;
    /** Open mode "PDF erzeugen" on the OPENED (saved) Variant; absent hides the action. */
    onGenerateOpened?: () => void;
    /** Reports a finished merge re-seed with the number of adopted field values. */
    onPrefillMerged?: (changedFieldCount: number) => void;
    /**
     * The Gebäudefoto taken with "Save map image" on the page's building map
     * (GEN-778) — the masked JPEG data URI. Each new capture bumps `version`;
     * the form adopts it as the `buildingPhoto` value like a user edit
     * (USER_PROVIDED), so it survives merge re-seeds and travels with save,
     * preview and /calculate exactly like the former upload did.
     */
    capturedPhoto?: { dataUri: string; version: number } | null;
    /**
     * True while the host re-resolves the prefill (a map-subset change, from
     * the toggle on): the missing-building-geometry warning waits for the
     * merged values instead of judging the outgoing ones, and the save-shaped
     * actions wait too — the server re-resolves a save from the new subset.
     */
    prefillLoading?: boolean;
    onCancel: () => void;
}

/** Normalizes empty-ish values so prefill/state comparisons are stable. */
function normalize(value: unknown): unknown {
    if (value === undefined || value === '') return null;
    return value;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * [normalize], extended to containers: empty entries of a list are dropped
 * (an empty list is null) and an object without any value (a cleared
 * consumption pair) is null.
 */
function normalizeDeep(value: unknown): unknown {
    const normalized = normalize(value);
    if (Array.isArray(normalized)) {
        const entries = normalized.filter((entry) => normalizeDeep(entry) !== null);
        return entries.length === 0 ? null : entries;
    }
    if (isPlainObject(normalized)) {
        return Object.values(normalized).every((entry) => normalizeDeep(entry) === null) ? null : normalized;
    }
    return normalized;
}

/** The number a value stands for: a finite number, or numeric text ("12.50", "0,41"); else null. */
function numericValue(value: unknown): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string') return null;
    const parsed = parseDecimalInput(value);
    return parsed !== null && Number.isFinite(parsed) ? parsed : null;
}

/**
 * Type-tolerant equality between the current form value and the prefilled one —
 * mirrors the backend stamp diff's tolerance, so a value changed and changed
 * BACK compares equal again (its badge returns to the original source): empty
 * values ('' / [] / a cleared pair) are null, numbers compare by value across
 * the JSON/string boundary ("12.50" = 12.5, "0,41" = 0.41), lists by content
 * and order-insensitively (every list field is a set: multi-select codes,
 * year-tagged consumption entries), objects key by key whatever the key order.
 */
function valuesEqual(a: unknown, b: unknown): boolean {
    const left = normalizeDeep(a);
    const right = normalizeDeep(b);
    if (left === null || right === null) return left === right;
    if (Array.isArray(left) || Array.isArray(right)) {
        if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
        const unmatched = [...right];
        return left.every((entry) => {
            const index = unmatched.findIndex((candidate) => valuesEqual(entry, candidate));
            if (index < 0) return false;
            unmatched.splice(index, 1);
            return true;
        });
    }
    if (isPlainObject(left) || isPlainObject(right)) {
        if (!isPlainObject(left) || !isPlainObject(right)) return false;
        const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
        return [...keys].every((key) => valuesEqual(left[key], right[key]));
    }
    if (left === right) return true;
    if (typeof left === 'number' || typeof right === 'number') {
        const leftNumber = numericValue(left);
        return leftNumber !== null && leftNumber === numericValue(right);
    }
    return String(left) === String(right);
}

const BUILDING_FUNCTION_NAMES = BUILDING_FUNCTIONS_MAPPING as Record<string, string>;

/** The Gebäudetyp options: the fixed Steckbrief list, sorted by register name. */
const BUILDING_FUNCTION_OPTIONS = STECKBRIEF_BUILDING_FUNCTION_CODES.map((code) => ({
    code,
    name: BUILDING_FUNCTION_NAMES[code] ?? code,
})).sort((a, b) => a.name.localeCompare(b.name, 'de'));

function initialValues(
    prefill: VariantPrefillDto,
    assumptionDefaults: SteckbriefAssumptionDefaultsDto | null,
): Record<string, unknown> {
    const values: Record<string, unknown> = {};
    for (const field of STECKBRIEF_VARIANT_FIELDS) {
        values[field.key] = normalize(prefill.fields[field.key]?.value);
    }
    // GEN-750: carried legacy values (non-model prefill keys the backend
    // mapping still owns, e.g. a pre-GEN-750 `windowReplacementYear`) enter the
    // values map too: no field entry exists for them, so they are never
    // rendered and nothing can edit them — but they flow into every
    // `/calculate` run (the preview must see the same facts as the eventual
    // stamp) and pass through the save payload verbatim (handleSave).
    for (const key of carriedLegacyKeys(Object.keys(prefill.fields))) {
        values[key] = normalize(prefill.fields[key].value);
    }
    // GEN-798 follow-up: the engine-derived surfaces (window = real × share,
    // facade = real − window) are display-only — always null in form state, so
    // every /calculate run derives them. A carried or stamped value (from the
    // time the fields were editable) must not freeze the derivation.
    for (const key of ENGINE_ECHO_FIELD_KEYS) {
        values[key] = null;
    }
    // GEN-802: the defaulted fields (the window-area share) seed the model
    // default of the prefilled Gebäudetyp as a visible, editable value whenever
    // the prefill carries nothing — create mode without a carried value, an
    // opened Variant whose share was a default (DEFAULT — the per-Gebäudetyp
    // one since GEN-802, or the fixed GEN-801 35), and an opened legacy Variant (see stampPrefillFields).
    // A carried/stamped user value wins. Runs after the prefill loop, so
    // `buildingFunction` is set (an out-of-list code was already dropped → the
    // base default). This replaces the fixed GEN-801 seed of 35.
    Object.assign(values, seedAssumptionDefaults(assumptionDefaults, values));
    // QA feedback round: the investment cost factor seeds its default (100 % =
    // the recommended costs) — a fixed value, not a per-Gebäudetyp assumption.
    if (values['investmentCostFactorPercent'] == null) {
        values['investmentCostFactorPercent'] = DEFAULT_INVESTMENT_COST_FACTOR_PERCENT;
    }
    // GEN-786: the Stand seeds the CURRENT month whenever the prefill carries
    // none — always in create mode (the backend never carries the previous
    // Variant's month forward) and for an opened legacy Variant saved before the
    // field existed. An opened Variant's stamped month wins. The seed stamps
    // USER_PROVIDED on save.
    if (values['asOfDate'] == null) {
        values['asOfDate'] = currentAsOfMonth();
    }
    // GEN-801: the two gates answer themselves from the data when the prefill
    // carries no answer (create mode without a carried one, Variants saved
    // before the gates existed) — an NGF means "known", a BGF alone "not known";
    // prefilled sources mean renewables exist. Nothing to go on → unanswered.
    if (values['netFloorAreaKnown'] == null) {
        values['netFloorAreaKnown'] =
            values['netFloorArea'] != null ? true : values['grossFloorArea'] != null ? false : null;
    }
    const sources = values['renewableEnergySources'];
    if (values['existingRenewableEnergy'] == null && Array.isArray(sources) && sources.length > 0) {
        values['existingRenewableEnergy'] = true;
    }
    // QA feedback round: the heating-year gate answers itself ja when the
    // prefill carries a heating year but no answer; otherwise unanswered.
    return seedHeatingYearGate(values);
}

/** A measure list without the given measures — null stays null (follow the engine). */
function withoutMeasures(measures: MeasureKey[] | null, excluded: MeasureKey[]): MeasureKey[] | null {
    if (measures == null || !measures.some((measure) => excluded.includes(measure))) return measures;
    return measures.filter((measure) => !excluded.includes(measure));
}

/**
 * The [SteckbriefVariantField.explicitAnswer] fields whose null already IS an
 * answer ("unknown") when the form opens (GEN-801): every one of an opened
 * Variant (its stamp was saved as it is), otherwise those a previous Variant
 * carried as a USER_PROVIDED null. A null from nowhere — the catalog had
 * nothing — is unanswered: nothing is preselected and a required field asks
 * for a choice. A non-null value is an answer anyway.
 */
function initialAnsweredKeys(prefill: VariantPrefillDto, opened: boolean): Set<string> {
    const answered = new Set<string>();
    for (const field of STECKBRIEF_VARIANT_FIELDS) {
        if (!field.explicitAnswer) continue;
        if (opened || prefill.fields[field.key]?.source === USER_PROVIDED_SOURCE) answered.add(field.key);
    }
    return answered;
}

/**
 * The value of a HIDDEN conditional field in every payload (GEN-801): null —
 * except the renewable sources behind "Existing renewable energy? nein", which
 * travel as the empty list: none exist, which the engine reads as "no PV"
 * (null means unknown and fires the PV assumption).
 */
function hiddenFieldValue(key: string, values: Record<string, unknown>): unknown {
    return key === 'renewableEnergySources' && values['existingRenewableEnergy'] === false ? [] : null;
}

/**
 * The values a calculation runs on (GEN-801): hidden conditional fields
 * replaced exactly like in the save payload — a NGF the advisor declared
 * unknown must not feed the prognosis the PDF then prints without it.
 */
function calculationValues(values: Record<string, unknown>): Record<string, unknown> {
    const next = { ...values };
    for (const field of STECKBRIEF_VARIANT_FIELDS) {
        if (!fieldVisibleIn(field, values)) next[field.key] = hiddenFieldValue(field.key, values);
    }
    return next;
}

function formatNumber(value: number | null | undefined, fractionDigits = 1): string {
    if (value === null || value === undefined) return '–';
    return value.toLocaleString('de-DE', { maximumFractionDigits: fractionDigits });
}

type DecimalParser = (text: string) => number | null;
type DecimalFormatter = (value: number, language: string | undefined) => string;

/** The text a decimal input shows for a form value: the locale number, typed text verbatim, else empty. */
function decimalText(value: unknown, language: string, format: DecimalFormatter = formatDecimalInput): string {
    if (typeof value === 'number') return format(value, language);
    return typeof value === 'string' ? value : '';
}

/**
 * The form value typed decimal text stands for: null when blank, the number —
 * or the text itself when it is no number, so the values schema blocks the save.
 */
function decimalValue(text: string, parse: DecimalParser = parseDecimalInput): unknown {
    const parsed = parse(text);
    return parsed !== null && Number.isNaN(parsed) ? text : parsed;
}

/**
 * Input of a `decimalInput` NUMBER field (GEN-788, the tariffs in €/kWh): a
 * text input accepting the German decimal comma as well as the point, instead
 * of the browser's locale-dependent number input. It keeps the TYPED text while
 * the advisor edits ("0,40" never collapses to "0,4") and writes the parsed
 * value; a value changed from outside (a merge re-seed) replaces the text. The
 * label always sits above the input, so the EVEBI-default placeholder shows.
 * `parse` / `format` swap the number syntax: the kWh consumption inputs read
 * and show German thousands points ("48.463,3").
 */
function DecimalNumberField({
    value,
    language,
    onChange,
    parse = parseDecimalInput,
    format = formatDecimalInput,
    ...textFieldProps
}: {
    value: unknown;
    language: string;
    onChange: (next: unknown) => void;
    parse?: DecimalParser;
    format?: DecimalFormatter;
} & Pick<TextFieldProps, 'label' | 'error' | 'helperText' | 'placeholder' | 'sx'>) {
    const [text, setText] = useState(() => decimalText(value, language, format));
    // Derived state (React's "previous props" pattern): only an outside change
    // resets the text — the echo of this input's own edit leaves it as typed.
    const [seenValue, setSeenValue] = useState(value);
    if (!Object.is(value, seenValue)) {
        setSeenValue(value);
        if (!Object.is(decimalValue(text, parse), value)) setText(decimalText(value, language, format));
    }
    return (
        <TextField
            {...textFieldProps}
            size="small"
            value={text}
            onChange={(e) => {
                setText(e.target.value);
                onChange(decimalValue(e.target.value, parse));
            }}
            slotProps={{ htmlInput: { inputMode: 'decimal' }, inputLabel: { shrink: true } }}
        />
    );
}

/**
 * Per-field provenance badge.
 *
 * Deliberately NOT the merged `src/common/catalog-prefill` CatalogField: that
 * render prop binds to the generic `/v1/documents/data-catalog/resolve` endpoint
 * through its own provider/hook and models source only as resolved/unresolved —
 * it cannot carry this form's three-state source (catalog key vs USER_PROVIDED
 * vs empty) from the `/variants/prefill` response without faking a prefill
 * result object, so a small local badge is used instead.
 */
function SourceBadge({ source, title }: { source: string | null; title?: string }) {
    const { t } = useTranslation();
    // Empty state (no value from anyone): no badge — an empty control is self-evident.
    if (source === null) return null;
    if (source === USER_PROVIDED_SOURCE) {
        return <Chip size="small" variant="outlined" color="primary" label={t('User provided')} />;
    }
    // GEN-801: a value still at its default (the window-area share). Since
    // GEN-802 that is the configured default of the current Gebäudetyp —
    // `title` explains at which level it was resolved.
    if (source === DEFAULT_SOURCE) {
        return (
            <Tooltip title={title ?? ''}>
                <Chip size="small" variant="outlined" label={t('Default')} />
            </Tooltip>
        );
    }
    return (
        <Tooltip title={t('Prefilled from the data catalog key "{{key}}"', { key: source })}>
            <Chip size="small" variant="outlined" label={source} />
        </Tooltip>
    );
}

/**
 * Colour of a measure level: Prio and Empfohlen differ in intensity of the
 * same primary hue (a ranking, not two categories); Nicht empfohlen is neutral.
 */
function LevelSwatch({ level, round = false }: { level: MeasureLevel; round?: boolean }) {
    return (
        <Box
            component="span"
            sx={{
                flex: '0 0 auto',
                width: round ? 8 : 10,
                height: round ? 8 : 10,
                borderRadius: round ? '50%' : 0.5,
                ...(level === 'PRIO' && { bgcolor: 'primary.main' }),
                ...(level === 'RECOMMENDED' && { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.35) }),
                ...(level === 'NOT_RECOMMENDED' && { border: 1, borderStyle: 'dashed', borderColor: 'text.disabled' }),
            }}
        />
    );
}

/** Selected-state styling of one switch segment, matching the LevelSwatch colours. */
function levelButtonSx(level: MeasureLevel): SxProps<Theme> {
    return {
        flex: { xs: 1, sm: 'none' },
        px: 1.25,
        py: 0.5,
        textTransform: 'none',
        whiteSpace: 'nowrap',
        '&.Mui-selected, &.Mui-selected:hover': {
            fontWeight: 600,
            ...(level === 'PRIO' && { bgcolor: 'primary.main', color: 'primary.contrastText' }),
            ...(level === 'RECOMMENDED' && {
                bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.16),
                color: 'primary.dark',
            }),
            ...(level === 'NOT_RECOMMENDED' && { bgcolor: 'action.selected', color: 'text.primary' }),
        },
    };
}

/** One row of the Maßnahmen review list: the system proposal next to the advisor's classification. */
interface MeasureRowView {
    measure: MeasureKey;
    system: MeasureLevel;
    level: MeasureLevel;
    /** The engine's proposal entry (rationale tooltip, fallback marker); absent when not proposed. */
    proposed: ProposedMeasureDto | undefined;
}

/**
 * Builds the review list rows.
 *
 * SYSTEM level — what the engine recommends for the current facts,
 * independent of anything the advisor set: proposed measures are Empfohlen,
 * those the engine's Prio rules flag (`systemPrio` — never the `prio` echo of
 * the advisor's override, GEN-754) Prio, everything else Nicht empfohlen.
 *
 * EFFECTIVE level — without any selection or priority override it IS the
 * system level. Otherwise it follows the explicit state, except a stamped
 * selection WITHOUT a priority override (an opened pre-GEN-754 Variant),
 * whose Prio flags the backend computes: those are read from the echo.
 *
 * UNAVAILABLE measures (the heat pump with Fernwärme, [unavailableMeasures])
 * are Nicht empfohlen on both sides — also while the displayed result is still
 * the one computed before the carrier changed: the engine never proposes them
 * for the current facts, and the backend drops them from any selection.
 */
function buildMeasureRowViews(
    calc: SteckbriefCalculationResultDto,
    rows: MeasureKey[],
    selectedMeasures: MeasureKey[] | null,
    priorityOverride: MeasureKey[] | null,
    unavailable: MeasureKey[] = [],
): MeasureRowView[] {
    const selected = selectedMeasures ?? calc.selectedMeasures;
    return rows.map((measure) => {
        if (unavailable.includes(measure)) {
            const proposed = calc.proposedMeasures.find((m) => m.measure === measure);
            return { measure, system: 'NOT_RECOMMENDED', level: 'NOT_RECOMMENDED', proposed };
        }
        const index = calc.proposedMeasures.findIndex((m) => m.measure === measure);
        const system: MeasureLevel =
            index < 0 ? 'NOT_RECOMMENDED' : calc.proposedMeasures[index].systemPrio ? 'PRIO' : 'RECOMMENDED';
        let level: MeasureLevel;
        if (selectedMeasures === null && priorityOverride === null) level = system;
        else if (!selected.includes(measure)) level = 'NOT_RECOMMENDED';
        else {
            const prio =
                priorityOverride !== null
                    ? priorityOverride.includes(measure)
                    : calc.priorityMeasures.includes(MEASURE_CHIP_LABELS[measure]);
            level = prio ? 'PRIO' : 'RECOMMENDED';
        }
        return { measure, system, level, proposed: index < 0 ? undefined : calc.proposedMeasures[index] };
    });
}

/**
 * The Maßnahmen review list: one row per catalogue measure, always the same
 * rows in the same order. Left the SYSTEM proposal (never changes with the
 * advisor's input), middle the measure, right the advisor's classification
 * as a single three-way switch. A row diverging from the proposal gets a
 * rail, a "changed" tag and a per-row undo; the summary under the list
 * counts the levels and the divergences.
 */
const MeasuresReviewList = memo(function MeasuresReviewList({
    rows,
    result,
    reasons,
    reasonErrors,
    unavailable,
    onClassificationChange,
    onReasonChange,
}: {
    rows: MeasureRowView[];
    result: SteckbriefCalculationResultDto;
    /** Measures that cannot be recommended for the current facts — only "Not recommended" is offered. */
    unavailable: MeasureKey[];
    /** The advisor's reason per changed row — internal, stamped with the Variant, never printed. */
    reasons: MeasureTextMap;
    reasonErrors: MeasureTextMap;
    onClassificationChange: (selected: MeasureKey[] | null, priority: MeasureKey[] | null) => void;
    onReasonChange: (measure: MeasureKey, reason: string) => void;
}) {
    const { t, i18n } = useTranslation();
    const counts = countLevels(rows);
    // i18next key of the last rejected change's message (MEASURE_LEVEL_LIMITS).
    const [limitMessageKey, setLimitMessageKey] = useState<string | null>(null);
    // A classification changed from outside the switch (reset to the system
    // proposal) makes the message stale — drop it. Keyed on the levels, not on
    // `rows`, which a value-edit recalculation re-creates without changing them.
    const levelSignature = rows.map((row) => row.level).join(',');
    const [messageLevelSignature, setMessageLevelSignature] = useState(levelSignature);
    if (messageLevelSignature !== levelSignature) {
        setMessageLevelSignature(levelSignature);
        setLimitMessageKey(null);
    }
    const changedCount = rows.filter((row) => row.level !== row.system).length;

    /**
     * Sets one row's classification. The whole list is re-derived into the
     * explicit `/calculate` inputs — selected = the non-NOT_RECOMMENDED rows,
     * priority override = the PRIO rows — so what the list shows is exactly
     * what gets printed (no computed top-N silently promoting another measure
     * once one is dropped). A list matching the system proposal row for row
     * collapses back to `null, null`: the engine defaults, byte-identical to
     * an untouched panel. A change breaking the per-level limits is rejected
     * and explained instead.
     */
    function handleLevelChange(measure: MeasureKey, level: MeasureLevel) {
        const next = rows.map((row) => (row.measure === measure ? { ...row, level } : row));
        // A rejected change leaves everything — the row's reason included — untouched.
        const violation = measureLimitViolation(counts, countLevels(next));
        setLimitMessageKey(violation);
        if (violation !== null) return;
        // A row back at the system proposal needs no reason — drop it and any
        // inline error, so a later change starts clean instead of stale.
        if (next.some((row) => row.measure === measure && row.level === row.system)) {
            onReasonChange(measure, '');
        }
        if (next.every((row) => row.level === row.system)) {
            onClassificationChange(null, null);
            return;
        }
        onClassificationChange(
            next.filter((row) => row.level !== 'NOT_RECOMMENDED').map((row) => row.measure),
            next.filter((row) => row.level === 'PRIO').map((row) => row.measure),
        );
    }
    const rowGrid = {
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '150px 1fr auto' },
        gap: { xs: 1, sm: 2 },
    };
    return (
        <Stack spacing={1}>
            <Stack
                direction="row"
                spacing={2}
                useFlexGap
                sx={{ flexWrap: 'wrap', typography: 'caption', color: 'text.secondary' }}
                data-testid="measures-legend"
            >
                {MEASURE_LEVELS.map((level) => (
                    <Stack key={level} direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                        <LevelSwatch level={level} />
                        <span>{t(MEASURE_LEVEL_LABEL_KEYS[level])}</span>
                    </Stack>
                ))}
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: 0.5,
                            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.16),
                            border: 1,
                            borderColor: 'warning.main',
                        }}
                    />
                    <span>{t('Deviates from the system proposal')}</span>
                </Stack>
            </Stack>
            <Box>
                <Box
                    sx={{
                        ...rowGrid,
                        display: { xs: 'none', sm: 'grid' },
                        px: 1.25,
                        pb: 0.75,
                        typography: 'overline',
                        lineHeight: 1.5,
                        color: 'text.secondary',
                    }}
                >
                    <span>{t('System proposal')}</span>
                    <span>{t('Measure')}</span>
                    <span>{t('Your classification')}</span>
                </Box>
                {rows.map(({ measure, system, level, proposed }) => {
                    const changed = level !== system;
                    // A retired measure (legacy stamp) is one-way: it can be
                    // dropped, never re-added — admi no longer offers it (GEN-777).
                    const retired = RETIRED_MEASURE_KEYS.includes(measure);
                    // The heat pump with Fernwärme (QA feedback round): never
                    // recommended, never Prio — only "Not recommended" is offered.
                    const isUnavailable = unavailable.includes(measure);
                    // Code-based translation (GEN-758) — the response's German
                    // `chipLabel` stays a print/template contract. A measure due
                    // only in a later year (the heat pump of a young heating,
                    // QA feedback round) names that year: "Wärmepumpe (2035)".
                    const dueYear = proposed?.dueYear ?? null;
                    const label =
                        dueYear === null ? measureLabel(t, measure) : `${measureLabel(t, measure)} (${dueYear})`;
                    const rationale = proposed ? measureRationale(t, i18n.language, proposed) : null;
                    const nameText = (
                        <Typography variant="body2" sx={{ fontWeight: 500 }} data-testid={`measure-name-${measure}`}>
                            {proposed?.usedFallbacks ? `${label} *` : label}
                        </Typography>
                    );
                    return (
                        <Box
                            key={measure}
                            data-testid={`measure-row-${measure}`}
                            data-changed={changed}
                            sx={{
                                ...rowGrid,
                                alignItems: 'center',
                                px: 1.25,
                                py: 1,
                                borderTop: 1,
                                borderColor: 'divider',
                                ...(changed && {
                                    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.06),
                                    boxShadow: (theme) => `inset 2px 0 0 ${theme.palette.warning.main}`,
                                }),
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={0.75}
                                sx={{ alignItems: 'center', typography: 'body2', color: 'text.secondary' }}
                                data-testid={`measure-system-${measure}`}
                            >
                                <LevelSwatch level={system} round />
                                <span>{t(MEASURE_LEVEL_LABEL_KEYS[system])}</span>
                            </Stack>
                            <Stack
                                direction="row"
                                spacing={1}
                                useFlexGap
                                sx={{ alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}
                            >
                                {rationale === null ? (
                                    nameText
                                ) : (
                                    <Tooltip
                                        title={
                                            proposed?.usedFallbacks
                                                ? `${rationale} — ${t('Based on fallback assumptions.')}`
                                                : rationale
                                        }
                                    >
                                        {nameText}
                                    </Tooltip>
                                )}
                                {retired && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {t('No longer offered')}
                                    </Typography>
                                )}
                                {isUnavailable && (
                                    <Typography
                                        variant="caption"
                                        sx={{ color: 'text.secondary' }}
                                        data-testid={`measure-unavailable-${measure}`}
                                    >
                                        {t(HEAT_PUMP_DISTRICT_HEATING_HINT_KEY)}
                                    </Typography>
                                )}
                                {changed && (
                                    <>
                                        <Chip size="small" color="warning" variant="outlined" label={t('changed')} />
                                        <Button
                                            size="small"
                                            color="warning"
                                            onClick={() => handleLevelChange(measure, system)}
                                            aria-label={t('Reset {{measure}} to the system proposal', {
                                                measure: label,
                                            })}
                                            data-testid={`measure-undo-${measure}`}
                                            sx={{
                                                minWidth: 0,
                                                py: 0,
                                                textTransform: 'none',
                                                textDecoration: 'underline',
                                            }}
                                        >
                                            {t('undo')}
                                        </Button>
                                    </>
                                )}
                            </Stack>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={level}
                                onChange={(_, next: MeasureLevel | null) => {
                                    if (next !== null) handleLevelChange(measure, next);
                                }}
                                aria-label={t('Classification: {{measure}}', { measure: label })}
                                sx={{ width: { xs: '100%', sm: 'auto' } }}
                            >
                                {MEASURE_LEVELS.map((option) => (
                                    <ToggleButton
                                        key={option}
                                        value={option}
                                        disabled={
                                            option !== 'NOT_RECOMMENDED' &&
                                            (isUnavailable || (retired && level === 'NOT_RECOMMENDED'))
                                        }
                                        disableRipple
                                        data-testid={`measure-level-${measure}-${option}`}
                                        sx={levelButtonSx(option)}
                                    >
                                        {t(MEASURE_LEVEL_LABEL_KEYS[option])}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                            {/* A changed classification needs a reason (required at
                                save). Internal only: stamped with the Variant under
                                `measures.reasons`, never a form value, never printed. */}
                            {changed && (
                                <TextField
                                    size="small"
                                    fullWidth
                                    required
                                    id={`measure-reason-${measure}`}
                                    label={t('Reason for the change')}
                                    value={reasons[measure] ?? ''}
                                    onChange={(e) => onReasonChange(measure, e.target.value)}
                                    error={Boolean(reasonErrors[measure])}
                                    helperText={
                                        reasonErrors[measure]
                                            ? t(reasonErrors[measure])
                                            : t('Internal only — saved with the variant, not printed.')
                                    }
                                    slotProps={{
                                        htmlInput: {
                                            maxLength: MEASURE_REASON_MAX_LENGTH,
                                            'data-testid': `measure-reason-${measure}`,
                                        },
                                    }}
                                    sx={{ gridColumn: '1 / -1' }}
                                />
                            )}
                        </Box>
                    );
                })}
            </Box>
            {limitMessageKey !== null && (
                <Alert severity="warning" onClose={() => setLimitMessageKey(null)} data-testid="measures-limit-message">
                    {t(limitMessageKey)}
                </Alert>
            )}
            {result.proposedMeasures.some((m) => m.usedFallbacks) && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('* Proposal based on fallback assumptions for unknown facts.')}
                </Typography>
            )}
            <Stack
                direction="row"
                spacing={2}
                useFlexGap
                sx={{ alignItems: 'center', flexWrap: 'wrap', typography: 'body2', color: 'text.secondary' }}
                data-testid="measures-summary"
            >
                <span>
                    {t('{{prio}} Prio · {{recommended}} recommended · {{notRecommended}} not recommended', {
                        prio: counts.PRIO,
                        recommended: counts.RECOMMENDED,
                        notRecommended: counts.NOT_RECOMMENDED,
                    })}
                </span>
                <Chip
                    size="small"
                    color={changedCount > 0 ? 'warning' : 'default'}
                    variant="outlined"
                    label={
                        changedCount > 0
                            ? t('Deviations from the system proposal: {{count}}', { count: changedCount })
                            : t('Matches the system proposal')
                    }
                    data-testid="measures-deviation"
                />
            </Stack>
        </Stack>
    );
});

/**
 * The Steckbrief Variant form (RFC-004 §5 step 3, GEN-701): the Variant name +
 * kind switch at the top, sections mirroring the print template blocks,
 * per-field source badges from the prefill response (flipping to USER_PROVIDED
 * live on edit), the measures panel with the KPI preview backed by
 * `POST /calculate` (on demand via "(Re-)calculate" like PV Analysis — the
 * result is outlined orange once the inputs drift from those it was computed
 * with; stale-guarded), and the save handoff building the exact
 * `CreateVariantRequest` contract shape.
 */
export function SteckbriefVariantForm({
    steckbrief,
    prefill,
    prefillVersion = 0,
    assumptionDefaults = null,
    initialKind = 'FINAL',
    initialSelectedMeasures = null,
    initialHeatingOverride = null,
    initialPriorityOverride = null,
    initialMeasureReasons,
    initialPvEstimateId = null,
    openedVariant = null,
    activeVariantNames = [],
    saving,
    previewing = false,
    generating = false,
    onSave,
    onSaveAndGenerate,
    onPreview,
    preview = null,
    previewError = null,
    previewFileName = 'steckbrief-preview.pdf',
    onGenerateOpened,
    onPrefillMerged,
    capturedPhoto = null,
    prefillLoading = false,
    onCancel,
}: SteckbriefVariantFormProps) {
    // i18n: the GEN-780 KPI investment totals format per UI language.
    const { t, i18n } = useTranslation();

    const [kind, setKind] = useState<SteckbriefKind>(initialKind);
    // Read by /calculate through the ref, like the measures state (GEN-806).
    const kindRef = useRef<SteckbriefKind>(initialKind);
    // Variant name — inline in the form (GEN-701 QA feedback: the pre-step page
    // asking for the name before the form is gone), mirroring the PV Analysis
    // "Variation Name" field. Default empty; required at save — the backend
    // also 400s on a blank name, the client-side check keeps the error inline.
    // In open mode the field STAYS empty (placeholder = source Variant's name):
    // saving always creates a NEW Variant, and active names are unique (GEN-763).
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState<string | null>(null);
    // GEN-786: derived numbers seed rounded — value and badge baseline alike.
    const [seedPrefill] = useState(() => normalizeVariantPrefill(prefill));
    // GEN-802: the model defaults, read once at mount — a stable identity for
    // the callbacks below.
    const [assumptionConfig] = useState(assumptionDefaults);
    const [values, setValues] = useState<Record<string, unknown>>(() => initialValues(seedPrefill, assumptionConfig));
    const [errors, setErrors] = useState<Record<string, string>>({});
    // GEN-801: the explicitAnswer fields the advisor answered — "unknown"
    // included, which is null like no answer at all (see initialAnsweredKeys).
    const [answeredKeys, setAnsweredKeys] = useState<Set<string>>(() =>
        initialAnsweredKeys(seedPrefill, openedVariant !== null),
    );
    // The last "(Re-)calculate" click was refused by the validation (GEN-801).
    const [calcBlocked, setCalcBlocked] = useState(false);
    // The last save-shaped click was refused by the inline checks — the footer
    // says so next to the buttons, which sit far below the marked fields.
    const [saveRefused, setSaveRefused] = useState(false);
    // The prefill baseline the source badges and the merge re-seed compare
    // against (GEN-765): seeded from the initial prefill, replaced wholesale by
    // each merge — NEVER read from the `prefill` prop directly after mount, so
    // a re-resolved prop cannot flip badges before its merge effect ran.
    const [prefillFields, setPrefillFields] = useState<Record<string, PrefilledVariantField>>(() => seedPrefill.fields);
    const prefillFieldsRef = useRef(seedPrefill.fields);
    const appliedPrefillVersionRef = useRef(prefillVersion);

    // Measures panel state: null selection = follow the engine's proposal.
    // Open mode seeds from the opened Variant's stamp (GEN-765) — minus a
    // measure the stamp's facts no longer allow (the heat pump of a Fernwärme
    // building, QA feedback round; the backend drops it too).
    const [selectedMeasures, setSelectedMeasures] = useState<MeasureKey[] | null>(() =>
        withoutMeasures(initialSelectedMeasures, unavailableMeasures(values)),
    );
    const [heatingOverride, setHeatingOverride] = useState<RecommendedHeatingSystemKey | null>(initialHeatingOverride);
    // Priority override (GEN-754): null = the computed top-N default. Once the
    // advisor edits a Prio flag, this becomes the explicit, STICKY Prio set —
    // deselecting a measure drops it, newly selected measures join without
    // prio, and the computed default never reasserts itself until reset.
    const [priorityOverride, setPriorityOverride] = useState<MeasureKey[] | null>(() =>
        withoutMeasures(initialPriorityOverride, unavailableMeasures(values)),
    );
    // The advisor's reason per changed classification, and the inline
    // "reason missing" errors of the last save attempt.
    const [measureReasons, setMeasureReasons] = useState<MeasureTextMap>(() => ({ ...initialMeasureReasons }));
    const [measureReasonErrors, setMeasureReasonErrors] = useState<MeasureTextMap>({});
    // The measure list is the fixed catalogue: the nine OFFERED measures, plus a
    // retired measure only when the opened Variant's stamp selected it (GEN-777
    // legacy replay). Fixed at mount, so no row ever appears or disappears
    // while the advisor works through the list.
    const [measureRows] = useState<MeasureKey[]>(() => [
        ...OFFERED_MEASURE_KEYS,
        ...(initialSelectedMeasures ?? []).filter((measure) => RETIRED_MEASURE_KEYS.includes(measure)),
    ]);
    // Refs mirroring the four `/calculate` inputs (values, selection, heating
    // override, priority override), updated in the same handlers as their
    // states: every calc run reads all four inputs from these refs, never from
    // its render closure, so a value arriving asynchronously (the map capture)
    // or a click right after a keystroke always calculates the latest inputs.
    const valuesRef = useRef(values);
    const selectedMeasuresRef = useRef<MeasureKey[] | null>(selectedMeasures);
    const heatingOverrideRef = useRef<RecommendedHeatingSystemKey | null>(initialHeatingOverride);
    const priorityOverrideRef = useRef<MeasureKey[] | null>(priorityOverride);

    const [calc, setCalc] = useState<SteckbriefCalculationResultDto | null>(null);
    const [calcLoading, setCalcLoading] = useState(false);
    const [calcError, setCalcError] = useState<string | null>(null);
    // The inputs the displayed `calc` was computed with — the drift baseline.
    const [calcInputs, setCalcInputs] = useState<CalcInputs | null>(null);

    // Stale-response guard (same idiom as the SteckbriefeTab load effect): only
    // the newest in-flight /calculate run may apply its response.
    const calcSeqRef = useRef(0);
    // The in-flight /calculate run's controller (null when idle): a new run
    // aborts the one it supersedes, and unmounting aborts the last — a first
    // run per input set computes the PV estimate (prod 2026-09-30: 41 s).
    const calcAbortRef = useRef<AbortController | null>(null);
    // The initial run's controller and guard — see the unmount effect below
    // and the initial-run effect further down.
    const initialCalcAbortRef = useRef<AbortController | null>(null);
    const didInitialCalcRef = useRef(false);

    // The PV estimate the form DISPLAYS (GEN-799): the opened Variant's stamped
    // one first, then whatever the latest /calculate response (or a refresh)
    // carries — its id travels on /calculate, the preview and the save, so the
    // server stamps exactly what the advisor saw.
    const pvEstimateIdRef = useRef<string | null>(initialPvEstimateId);
    // "Refresh PV estimate" in flight (GEN-799): the synchronous refresh request
    // plus the recalculation that follows it — one page action like a save.
    const [pvRefreshing, setPvRefreshing] = useState(false);
    const [pvRefreshError, setPvRefreshError] = useState<string | null>(null);

    const runCalculate = useCallback(
        (
            nextValues: Record<string, unknown>,
            nextSelected: MeasureKey[] | null,
            nextOverride: RecommendedHeatingSystemKey | null,
            nextPriority: MeasureKey[] | null,
        ) => {
            const seq = ++calcSeqRef.current;
            calcAbortRef.current?.abort();
            const controller = new AbortController();
            calcAbortRef.current = controller;
            const inputs: CalcInputs = {
                values: nextValues,
                selectedMeasures: nextSelected,
                heatingOverride: nextOverride,
                priorityOverride: nextPriority,
            };
            setCalcLoading(true);
            // Resolves once the run applied (or failed) — the PV refresh awaits
            // the recalculation it triggers; the other callers ignore it.
            return ensanSteckbriefService
                .calculateSteckbrief(
                    steckbrief.id,
                    {
                        values: calculationValues(nextValues),
                        selectedMeasures: nextSelected,
                        heatingSystemOverride: nextOverride,
                        priorityOverride: nextPriority,
                        pvEstimateId: pvEstimateIdRef.current,
                        // GEN-806: SALES estimates a missing NGF/BGF from the building model.
                        kind: kindRef.current,
                    },
                    { signal: controller.signal },
                )
                .then((result) => {
                    if (seq !== calcSeqRef.current) return;
                    // The response's estimate is the displayed one from now on
                    // (null: PV not selected, or a backend without the feature).
                    pvEstimateIdRef.current = result.pvEstimate?.estimateId ?? null;
                    setPvRefreshError(null);
                    setCalc(result);
                    setCalcInputs(inputs);
                    setCalcError(null);
                    setCalcLoading(false);
                })
                .catch((err: unknown) => {
                    // Aborted: superseded by a newer run (which owns the
                    // spinner) or unmounted — neither is a failed calculation.
                    if (axios.isCancel(err) || controller.signal.aborted) return;
                    if (seq !== calcSeqRef.current) return;
                    setCalcError(t('Calculation failed. The preview may be outdated.'));
                    setCalcLoading(false);
                })
                .finally(() => {
                    if (calcAbortRef.current === controller) calcAbortRef.current = null;
                });
        },
        [steckbrief.id, t],
    );

    // Unmounting aborts the in-flight run. StrictMode's simulated unmount
    // aborts the initial run too, and its guarded effect would not start it
    // again on the re-mount — so an aborted, unsettled initial run re-arms it.
    useEffect(
        () => () => {
            const inFlight = calcAbortRef.current;
            if (inFlight === null) return;
            inFlight.abort();
            calcAbortRef.current = null;
            if (inFlight === initialCalcAbortRef.current) didInitialCalcRef.current = false;
        },
        [],
    );

    /**
     * Drops the measures the given values make unavailable (the heat pump once
     * the carrier is Fernwärme, QA feedback round) from an EXPLICIT selection
     * and Prio set, with their reasons. A null selection (follow the engine)
     * stays null: the next run's proposal already excludes them — freezing it
     * into an explicit set would pin the Prio default of the old carrier.
     * Refs and setters only, so it is stable.
     */
    const dropUnavailableMeasures = useCallback((nextValues: Record<string, unknown>) => {
        const excluded = unavailableMeasures(nextValues);
        if (excluded.length === 0) return;
        const nextSelected = withoutMeasures(selectedMeasuresRef.current, excluded);
        if (nextSelected !== selectedMeasuresRef.current) {
            setSelectedMeasures(nextSelected);
            selectedMeasuresRef.current = nextSelected;
        }
        const nextPriority = withoutMeasures(priorityOverrideRef.current, excluded);
        if (nextPriority !== priorityOverrideRef.current) {
            setPriorityOverride(nextPriority);
            priorityOverrideRef.current = nextPriority;
        }
        setMeasureReasons((current) => {
            if (!excluded.some((measure) => measure in current)) return current;
            const next = { ...current };
            for (const measure of excluded) delete next[measure];
            return next;
        });
    }, []);

    /**
     * Merge re-seed on a re-resolved prefill (GEN-765 map-subset change). The
     * form seeds once via useState, so a new prefill must be merged explicitly:
     * per field, a current value still AT its previous prefill baseline adopts
     * the new value+source; a USER_PROVIDED edit keeps the user's value (its
     * badge stays USER_PROVIDED unless the value happens to equal the new
     * prefill). Carried legacy keys are user-untouchable, so they follow the
     * baseline rule too. Like any edit, the merge does not recalculate: a
     * value it changed marks the result outdated until "(Re-)calculate".
     */
    useEffect(() => {
        if (prefillVersion === appliedPrefillVersionRef.current) return;
        appliedPrefillVersionRef.current = prefillVersion;
        const merged = normalizeVariantPrefill(prefill);
        const previousFields = prefillFieldsRef.current;
        const nextValues = { ...valuesRef.current };
        let changedCount = 0;
        const keys = new Set<string>([
            ...STECKBRIEF_VARIANT_FIELDS.map((field) => field.key),
            ...carriedLegacyKeys(Object.keys(merged.fields)),
        ]);
        for (const key of keys) {
            // Derived display-only values stay null — see initialValues.
            if (isEngineEchoField(key)) continue;
            const newValue = normalize(merged.fields[key]?.value);
            const baselineValue = normalize(previousFields[key]?.value);
            if (valuesEqual(valuesRef.current[key], baselineValue)) {
                if (!valuesEqual(valuesRef.current[key], newValue)) changedCount += 1;
                nextValues[key] = newValue;
            }
        }
        // GEN-802: the defaulted fields are seeded, never prefilled — the
        // baseline rule above never moves them; they follow a merged Gebäudetyp
        // CHANGE like a pick: a value still at the previous Gebäudetyp's
        // default, or empty, takes the merged one's; an edit stays. A merge
        // that keeps the Gebäudetyp leaves them alone — a share the advisor
        // cleared stays empty. Not counted; a followed key's inline error
        // clears like on a pick.
        const followed = valuesEqual(valuesRef.current['buildingFunction'], nextValues['buildingFunction'])
            ? {}
            : followAssumptionDefaults(
                  assumptionConfig,
                  nextValues,
                  valuesRef.current['buildingFunction'],
                  nextValues['buildingFunction'],
              );
        Object.assign(nextValues, followed);
        const followedKeys = Object.keys(followed);
        if (followedKeys.length > 0) {
            setErrors((errors) => {
                if (!followedKeys.some((followedKey) => followedKey in errors)) return errors;
                const remaining = { ...errors };
                for (const followedKey of followedKeys) delete remaining[followedKey];
                return remaining;
            });
        }
        // The heating-year gate still unanswered follows the merged values like on mount.
        const seededValues = seedHeatingYearGate(nextValues);
        prefillFieldsRef.current = merged.fields;
        // The merge IS the reaction to a re-resolved prefill prop (versioned) —
        // not derivable during render: it compares against the previous baseline.
        setPrefillFields(merged.fields);
        setValues(seededValues);
        valuesRef.current = seededValues;
        dropUnavailableMeasures(seededValues);
        onPrefillMerged?.(changedCount);
    }, [prefillVersion, prefill, onPrefillMerged, dropUnavailableMeasures, assumptionConfig]);

    const handleValueChange = useCallback(
        (key: string, newValue: unknown) => {
            // From the ref, not the render closure: a value arriving asynchronously
            // (the map capture, GEN-778) must not drop edits made in the meantime.
            const current = valuesRef.current;
            const next = { ...current, [key]: normalize(newValue) };
            // GEN-802: a Gebäudetyp pick moves the defaulted fields (the window
            // area share) along while they are empty or still the previous
            // type's default; an edited value stays.
            const followed =
                key === 'buildingFunction'
                    ? followAssumptionDefaults(assumptionConfig, current, current['buildingFunction'], next[key])
                    : {};
            Object.assign(next, followed);
            setValues(next);
            valuesRef.current = next;
            // Any edit answers the field (GEN-801 — "unknown" too) and clears its
            // inline error — a followed default clears its field's error too; the
            // next calculate or save re-validates.
            setAnsweredKeys((answered) => (answered.has(key) ? answered : new Set(answered).add(key)));
            const clearedKeys = [key, ...Object.keys(followed)];
            setErrors((errors) => {
                if (!clearedKeys.some((cleared) => cleared in errors)) return errors;
                const remaining = { ...errors };
                for (const cleared of clearedKeys) delete remaining[cleared];
                return remaining;
            });
            if (key === 'heatingEnergyCarrier') dropUnavailableMeasures(next);
        },
        [assumptionConfig, dropUnavailableMeasures],
    );

    /**
     * "(Re-)calculate": validates like the save (GEN-801) — the inline errors
     * show and nothing is calculated while the values fail — then runs
     * `/calculate` on the current inputs, read through the refs.
     */
    function handleRecalculate() {
        const valid = validateValues();
        setCalcBlocked(!valid);
        if (!valid) return;
        runCalculate(
            valuesRef.current,
            selectedMeasuresRef.current,
            heatingOverrideRef.current,
            priorityOverrideRef.current,
        );
    }

    /**
     * Writes several form values at once — for discrete, confirmed edits like
     * an applied prognosis adjustment (GEN-782). The panel shows adjusted ??
     * system locally at once; the printed ranges of the result follow on
     * "(Re-)calculate" like for any edit. Clears the touched keys' inline errors.
     */
    function handleApplyValues(patch: Record<string, unknown>) {
        const next = { ...valuesRef.current };
        for (const [key, value] of Object.entries(patch)) next[key] = normalize(value);
        setValues(next);
        valuesRef.current = next;
        setErrors((current) => {
            const remaining = { ...current };
            for (const key of Object.keys(patch)) delete remaining[key];
            return remaining;
        });
    }

    /**
     * Stamps a Gebäudevergleich as the lookalike reference context (GEN-700 D4). Lineage only: no
     * lookalike figure enters the Prognose. A search is no "(Re-)calculate" click, so it never
     * validates the form (that would flag every empty required field): a current result is
     * refreshed at once so the staleness hint reflects the new fingerprint — like the kind
     * switch — and an outdated one follows on the advisor's next "(Re-)calculate".
     */
    function handleLookalikeReference(patch: Record<string, unknown>) {
        const wasCurrent = calcInputs !== null && !calcOutdated;
        handleApplyValues(patch);
        if (wasCurrent) {
            runCalculate(
                valuesRef.current,
                selectedMeasuresRef.current,
                heatingOverrideRef.current,
                priorityOverrideRef.current,
            );
        }
    }

    /**
     * Adopts each new map capture (GEN-778) as the `buildingPhoto` value —
     * keyed on the capture version, so a re-render never re-applies it (a
     * removed photo stays removed until the next capture). Seeded with the
     * version present at MOUNT on purpose: the form mounts per context (the
     * host clears its capture state on a Steckbrief/variant switch), so only
     * captures taken while this form is mounted are adopted — never one that
     * predates it.
     */
    const appliedCaptureVersionRef = useRef(capturedPhoto?.version ?? 0);
    useEffect(() => {
        if (!capturedPhoto || capturedPhoto.version === appliedCaptureVersionRef.current) return;
        appliedCaptureVersionRef.current = capturedPhoto.version;
        handleValueChange('buildingPhoto', capturedPhoto.dataUri);
    }, [capturedPhoto, handleValueChange]);

    /**
     * Live source per field: prefill source while unchanged, USER_PROVIDED once
     * edited. A defaulted field (GEN-802) shows what a save will stamp instead:
     * DEFAULT while it equals the current Gebäudetyp's default.
     */
    function sourceOf(key: string): string | null {
        const assumed = assumptionDefaultSource(assumptionConfig, key, values[key], values['buildingFunction']);
        if (assumed !== undefined) return assumed;
        const prefilled = prefillFields[key];
        const originalValue = normalize(prefilled?.value);
        if (valuesEqual(values[key], originalValue)) return prefilled?.source ?? null;
        return USER_PROVIDED_SOURCE;
    }

    // The only automatic run: the initial one with the prefilled values, so the
    // measure list and prognosis are there on open. Create mode: engine
    // defaults (null,null,null). Open mode: the opened Variant's seeded
    // measures state. Declared AFTER the value-seeding effects above (the
    // Gebäudetyp auto-fill) and reading through the refs, so a value they seed
    // on mount is part of this run instead of outdating it at once. Every
    // later run is the "(Re-)calculate" click.
    useEffect(() => {
        if (didInitialCalcRef.current) return;
        didInitialCalcRef.current = true;
        runCalculate(
            valuesRef.current,
            selectedMeasuresRef.current,
            heatingOverrideRef.current,
            priorityOverrideRef.current,
        );
        initialCalcAbortRef.current = calcAbortRef.current;
    }, [runCalculate]);

    /** GEN-763 active-name rule, checked inline: trimmed + case-insensitive. */
    function isNameTaken(trimmedName: string): boolean {
        const needle = trimmedName.toLowerCase();
        return activeVariantNames.some((existing) => existing.trim().toLowerCase() === needle);
    }

    // The measures the CURRENT facts make unavailable (the heat pump with
    // Fernwärme) — memoized on the carrier so the memoized list keeps its props.
    const heatingCarrier = values['heatingEnergyCarrier'];
    const unavailable = useMemo(() => unavailableMeasures({ heatingEnergyCarrier: heatingCarrier }), [heatingCarrier]);
    const effectiveSelected: MeasureKey[] = (selectedMeasures ?? calc?.selectedMeasures ?? []).filter(
        (measure) => !unavailable.includes(measure),
    );
    const proposedHeatingSystem =
        calc?.proposedMeasures.find((m) => m.measure === 'HEATING_RENEWAL')?.heatingSystem ?? null;
    const effectiveHeating = heatingOverride ?? proposedHeatingSystem;

    /** Per-row view of the measures list — recomputed only when the measures state changes. */
    const measureRowViews = useMemo(
        () => (calc ? buildMeasureRowViews(calc, measureRows, selectedMeasures, priorityOverride, unavailable) : []),
        [calc, measureRows, selectedMeasures, priorityOverride, unavailable],
    );

    /**
     * Applies a classification change from the review list: the explicit
     * `/calculate` inputs it derived (or `null, null` for a list back at the
     * system proposal) become the panel state — the prognosis is outdated
     * until "(Re-)calculate". Stable (setters and refs only), so the memoized
     * list does not re-render on unrelated form edits.
     */
    const handleClassificationChange = useCallback(
        (nextSelected: MeasureKey[] | null, nextPriority: MeasureKey[] | null) => {
            setSelectedMeasures(nextSelected);
            selectedMeasuresRef.current = nextSelected;
            setPriorityOverride(nextPriority);
            priorityOverrideRef.current = nextPriority;
        },
        [],
    );

    /** Stable, so the memoized review list does not re-render on unrelated form edits. */
    const handleMeasureReasonChange = useCallback((measure: MeasureKey, reason: string) => {
        setMeasureReasons((current) => {
            const next = { ...current };
            if (reason === '') delete next[measure];
            else next[measure] = reason;
            return next;
        });
        // Any edit clears the row's inline error; the next save re-validates.
        setMeasureReasonErrors((current) => {
            if (!current[measure]) return current;
            const next = { ...current };
            delete next[measure];
            return next;
        });
    }, []);

    /** The trimmed reason of every row currently diverging from the system proposal. */
    function changedMeasureReasons(): Record<string, string> {
        const reasons: Record<string, string> = {};
        for (const row of measureRowViews) {
            const reason = measureReasons[row.measure]?.trim();
            if (row.level !== row.system && reason) reasons[row.measure] = reason;
        }
        return reasons;
    }

    /**
     * The save gate for the review list: every row diverging from the system
     * proposal needs a reason. Fills the inline error map and brings the first
     * offending row into view — the list may sit far above the footer.
     */
    function validateMeasureReasons(): boolean {
        const missing = measureRowViews.filter(
            (row) => row.level !== row.system && !measureReasons[row.measure]?.trim(),
        );
        const nextErrors: MeasureTextMap = {};
        for (const row of missing) nextErrors[row.measure] = MEASURE_REASON_REQUIRED_MSG;
        setMeasureReasonErrors(nextErrors);
        if (missing.length > 0) {
            document.getElementById(`measure-reason-${missing[0].measure}`)?.scrollIntoView?.({ block: 'center' });
        }
        return missing.length === 0;
    }

    /**
     * Restores the whole measures panel to the system default recommendation
     * (GEN-755): selected = proposed set (the engine's default), no heating
     * override, no priority override — outdating the prognosis like any edit.
     */
    function handleResetToRecommendation() {
        setSelectedMeasures(null);
        selectedMeasuresRef.current = null;
        setHeatingOverride(null);
        heatingOverrideRef.current = null;
        setPriorityOverride(null);
        priorityOverrideRef.current = null;
        setMeasureReasons({});
        setMeasureReasonErrors({});
    }

    /**
     * The displayed result no longer matches the current inputs (PV Analysis'
     * dirty result): outlined orange, "(Re-)calculate" enabled, and the save /
     * preview actions blocked — they stamp the result's proposal and echoed
     * engine values, which must belong to the values they are saved with.
     */
    const calcOutdated = useMemo(
        () =>
            calcInputs !== null &&
            !calcInputsEqual(calcInputs, {
                values,
                selectedMeasures,
                heatingOverride,
                priorityOverride,
            }),
        [calcInputs, values, selectedMeasures, heatingOverride, priorityOverride],
    );

    /**
     * "Refresh PV estimate" (GEN-799): forces a new PV Calculator computation
     * for the current values (a retryable failure — calculator down, no roof
     * found, …). The refresh is synchronous: its 200 answer is the new stored
     * estimate (READY or FAILED), which becomes the displayed one, and a
     * recalculation with that id follows at once so the PV line, the totals
     * and the assumptions all come from the backend — nothing is patched
     * locally. Offered only while the inputs still match the displayed run;
     * the page is busy (calculate / save / preview disabled) until the
     * recalculation applied. A request error keeps the line as it is and shows
     * a dismissible warning.
     */
    async function handleRefreshPvEstimate() {
        setPvRefreshing(true);
        setPvRefreshError(null);
        try {
            const refreshed = await ensanSteckbriefService.refreshPvEstimate(
                steckbrief.id,
                calculationValues(valuesRef.current),
            );
            pvEstimateIdRef.current = refreshed.estimateId;
            await runCalculate(
                valuesRef.current,
                selectedMeasuresRef.current,
                heatingOverrideRef.current,
                priorityOverrideRef.current,
            );
        } catch {
            setPvRefreshError(t('Failed to refresh the PV estimate.'));
        } finally {
            setPvRefreshing(false);
        }
    }

    /** True while the panel already matches the defaults — the reset button is disabled then. */
    const panelAtDefaults = measureRowViews.every((row) => row.level === row.system) && heatingOverride === null;

    /** The KPI header's warning-count chip (GEN-788) — follows the latest /calculate run. */
    const warningSummary = prognosisWarningSummary(calc?.prognosisWarnings);

    /**
     * Which tariffs the displayed /calculate run had ENTERED (GEN-788) — the rest
     * were the EVEBI defaults. Read from that run's inputs, not the live values,
     * which may already be ahead of the result (GEN-790 on-demand recalculation).
     */
    const enteredEnergyPrices: Record<EnergyPriceFieldKey, boolean> = {
        heatPrice: isEnteredEnergyPrice(calcInputs?.values.heatPrice),
        electricityPrice: isEnteredEnergyPrice(calcInputs?.values.electricityPrice),
    };

    /**
     * The placeholder of an empty tariff field (GEN-788): the EVEBI default the
     * latest /calculate run applied — none while that run carried an entered
     * price (its echo is the advisor's own value), before the first prognosis,
     * or for a heat price the carrier has no default for.
     */
    function energyPricePlaceholder(key: string): string | undefined {
        if (!isEnergyPriceField(key) || enteredEnergyPrices[key]) return undefined;
        const price = calc?.prognosis?.[ENERGY_PRICE_PROGNOSIS_KEYS[key]];
        if (price == null) return undefined;
        return t('{{price}} €/kWh (EVEBI default)', { price: formatAssumptionNumber(i18n.language, price, 2) });
    }

    /**
     * Render condition of a field — shared by the field renderer and the save
     * payload so the two can never disagree on what the print can show.
     */
    function fieldVisible(field: SteckbriefVariantField): boolean {
        return fieldVisibleIn(field, values);
    }

    /**
     * Runs the kind-aware values schema on the CURRENT values (through the ref —
     * shared by calculate and save) with the answered set as context; fills the
     * inline error map and brings the first invalid field into view.
     */
    function validateValues(): boolean {
        const schema = buildVariantValuesSchema(kind);
        try {
            schema.validateSync(valuesRef.current, {
                abortEarly: false,
                context: { [ANSWERED_KEYS_CONTEXT]: answeredKeys },
            });
        } catch (validationError) {
            if (validationError instanceof ValidationError) {
                const nextErrors: Record<string, string> = {};
                for (const inner of validationError.inner) {
                    if (inner.path && !nextErrors[inner.path]) nextErrors[inner.path] = inner.message;
                }
                setErrors(nextErrors);
                const first = STECKBRIEF_VARIANT_FIELDS.find((field) => nextErrors[field.key] !== undefined);
                if (first) {
                    document
                        .querySelector(`[data-field-key="${first.key}"]`)
                        ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
                }
                return false;
            }
            throw validationError;
        }
        setErrors({});
        return true;
    }

    /**
     * EXACTLY the values a save would send — shared by the save actions AND the
     * document preview (GEN-762), so the previewed PDF can never diverge from
     * the PDF a save-then-generate would produce.
     */
    function buildPayloadValues(): Record<string, unknown> {
        const payloadValues: Record<string, unknown> = {};
        for (const field of STECKBRIEF_VARIANT_FIELDS) {
            // Hidden conditional fields (a conditional after its gate flipped
            // away from its value; GEN-801: see hiddenFieldValue) are NULLED in
            // the payload — the saved stamp must only claim values the print
            // can actually show. The typed value stays in form state, so
            // toggling the condition back restores it in the UI.
            payloadValues[field.key] = fieldVisible(field)
                ? (values[field.key] ?? null)
                : hiddenFieldValue(field.key, values);
        }
        // GEN-750: carried legacy values pass through VERBATIM — they were
        // written into `values` straight from the prefill (initialValues), have
        // no field entry so nothing renders or edits them, and the
        // fieldVisible-based nulling above only walks the field model, so it
        // can never touch them. E.g. a pre-GEN-750 `windowReplacementYear`
        // survives every save round-trip until the engine's precedence
        // (inline tri-state year first) makes it moot.
        for (const key of carriedLegacyKeys(Object.keys(prefillFields))) {
            payloadValues[key] = values[key] ?? null;
        }
        // GEN-774/GEN-798: the derived surfaces (window, facade) stamp the value
        // the latest /calculate run echoed — what the "Assumptions & derived
        // values" section showed. Generation replays the stamped value into the
        // same lattice as the explicit winner, so the PDF can never diverge from
        // what was shown.
        for (const key of ENGINE_ECHO_FIELD_KEYS) {
            payloadValues[key] = calc?.[key] ?? null;
        }
        return payloadValues;
    }

    /**
     * The two save-shaped actions ("Variante speichern", "Speichern & PDF
     * erzeugen") share validation, payload building and the GEN-763 name
     * handling: the inline check runs BEFORE the request, and a racing save's
     * 409 VARIANT_NAME_TAKEN maps onto the same inline field error.
     */
    async function handleSaveAction(save: (request: CreateVariantRequest) => Promise<VariantSaveResult>) {
        // Client-side name requiredness ahead of the backend's 400 on a blank
        // name (GEN-701 QA feedback) — checked alongside the values schema so
        // all inline errors show together.
        const trimmedName = name.trim();
        const nameProblem =
            trimmedName === ''
                ? t('Please enter a variant name.')
                : isNameTaken(trimmedName)
                  ? t(NAME_TAKEN_MSG_KEY)
                  : null;
        setNameError(nameProblem);

        const valuesOk = validateValues();
        const reasonsOk = validateMeasureReasons();
        const refused = !valuesOk || !reasonsOk || nameProblem !== null;
        setSaveRefused(refused);
        if (refused) return;
        const reasons = changedMeasureReasons();

        const result = await save({
            name: trimmedName,
            kind,
            values: buildPayloadValues(),
            // GEN-799: the estimate the form displayed — stamped as-is, never re-estimated at save.
            pvEstimateId: pvEstimateIdRef.current,
            measures: calc
                ? {
                      proposed: calc.proposedMeasures.map((m) => m.measure),
                      selected: effectiveSelected,
                      heatingSystem: {
                          proposed: proposedHeatingSystem,
                          selected: effectiveHeating,
                      },
                      // The explicit Prio set (GEN-754) — null while the advisor
                      // never edited priority (the computed default applies).
                      priorities: priorityOverride,
                      // Internal reasons of the changed rows — stamped, never printed.
                      reasons: Object.keys(reasons).length > 0 ? reasons : null,
                  }
                : null,
        });
        if (result === 'nameTaken') setNameError(t(NAME_TAKEN_MSG_KEY));
    }

    /**
     * The values pass the save schema — silently, without inline errors: the
     * automatic preview must not flag fields the user has not tried to save.
     */
    const valuesComplete = useMemo(
        () =>
            buildVariantValuesSchema(kind).isValidSync(values, { context: { [ANSWERED_KEYS_CONTEXT]: answeredKeys } }),
        [kind, values, answeredKeys],
    );

    /**
     * Inline "Steckbrief-Vorschau" (GEN-762 stateless render): re-rendered after
     * every successful calculation — the initial one, each "(Re-)calculate",
     * the one a PV refresh ends with (GEN-799) and the one a kind switch runs
     * (GEN-806; a different print). Sends the exact save payload values plus the measures
     * state the result was computed with, so the PDF is the one a
     * save-then-generate would produce. Skipped while the result is outdated or
     * required fields are missing; the panel says why.
     */
    const requestPreview = useEffectEvent(() => {
        if (!onPreview || calcOutdated || !valuesComplete) return;
        const request: PreviewSteckbriefDocumentRequest = {
            kind,
            values: buildPayloadValues(),
            selectedMeasures: selectedMeasuresRef.current,
            heatingSystemOverride: heatingOverrideRef.current,
            priorityOverride: priorityOverrideRef.current,
            // GEN-799: the estimate the form displays — the preview prints exactly that one.
            pvEstimateId: pvEstimateIdRef.current,
        };
        onPreview(request);
    });

    useEffect(() => {
        // Keyed on the successful run only (`calcInputs` is set per run, even when
        // the result is unchanged): an edit outdates the result and waits for
        // "(Re-)calculate" instead of rendering a PDF per keystroke, and a kind
        // switch re-runs the calculation (GEN-806), whose result renders it.
        if (calcInputs) requestPreview();
    }, [calcInputs]);

    /**
     * Whether a null is the advisor's "unknown" rather than no answer yet
     * (GEN-801): always for fields without [SteckbriefVariantField.explicitAnswer],
     * otherwise once answered — the unanswered control selects nothing.
     */
    function nullIsUnknown(field: SteckbriefVariantField): boolean {
        return !field.explicitAnswer || answeredKeys.has(field.key);
    }

    function renderTriState(field: SteckbriefVariantField) {
        const value = values[field.key];
        // GEN-748: a NUMBER is "saniert + year" — the year travels INLINE in the
        // tri-state value (the backend contract: SteckbriefFactsMapper reads a
        // numeric tri-state as the renovation year), so a prefilled/carried
        // numeric value renders as saniert-selected with the year populated.
        const inlineYear = typeof value === 'number' ? value : null;
        const renovation = field.triStateFlavor === 'RENOVATION';
        // GEN-801: "Is NGF known?" is ja/nein only — no unknown button.
        const withUnknown = field.triStateFlavor !== 'BOOLEAN';
        // GEN-788: teilsaniert is the exact PARTIAL value (no year) — RENOVATION rows only.
        const toggleValue =
            value === true || inlineYear !== null
                ? 'true'
                : renovation && value === RENOVATION_PARTIAL_VALUE
                  ? 'partial'
                  : value === false
                    ? 'false'
                    : withUnknown && nullIsUnknown(field)
                      ? 'unknown'
                      : null;
        const error = errors[field.key];
        return (
            <FormControl key={field.key} size="small" error={!!error} data-testid={`tri-state-${field.key}`}>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                    <FormLabel sx={{ typography: 'body2' }}>{t(field.labelKey)}</FormLabel>
                    <SourceBadge source={sourceOf(field.key)} />
                </Stack>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={toggleValue}
                    onChange={(_, next) => {
                        if (next === null) return; // ignore deselect clicks — "unbekannt" is an explicit option
                        // teilsaniert after saniert + year drops the year: PARTIAL carries none.
                        handleValueChange(
                            field.key,
                            next === 'true'
                                ? true
                                : next === 'partial'
                                  ? RENOVATION_PARTIAL_VALUE
                                  : next === 'false'
                                    ? false
                                    : null,
                        );
                    }}
                    aria-label={t(field.labelKey)}
                    // Four buttons on a RENOVATION row (GEN-788) — wrap instead of overflowing the grid cell.
                    sx={{ mt: 0.5, flexWrap: 'wrap' }}
                >
                    <ToggleButton value="true">{renovation ? t('renovated') : t('yes')}</ToggleButton>
                    {renovation && <ToggleButton value="partial">{t('partially renovated')}</ToggleButton>}
                    <ToggleButton value="false">{renovation ? t('unrenovated') : t('no')}</ToggleButton>
                    {withUnknown && <ToggleButton value="unknown">{t('unknown')}</ToggleButton>}
                </ToggleButtonGroup>
                {/* GEN-748: optional year input, revealed while saniert is selected
                    on a flagged item. Year filled → the NUMBER is the tri-state
                    value; cleared → plain saniert (true). Goes through
                    handleValueChange like every edit, so /calculate re-runs and
                    the source badge flips to USER_PROVIDED. */}
                {field.renovationYearInput && toggleValue === 'true' && (
                    <TextField
                        type="number"
                        size="small"
                        label={t('Renovation year (optional)')}
                        value={inlineYear ?? ''}
                        onChange={(e) => {
                            const text = e.target.value;
                            handleValueChange(field.key, text === '' ? true : Number(text));
                        }}
                        error={!!error}
                        sx={{ mt: 1, width: 200 }}
                    />
                )}
                {(error || field.helpTextKey) && (
                    <FormHelperText error={!!error}>{error ? t(error) : t(field.helpTextKey as string)}</FormHelperText>
                )}
            </FormControl>
        );
    }

    /**
     * A SELECT shown as buttons (GEN-801: Verglasung, Beleuchtung) — the
     * options plus a trailing "unknown" (null), like the renovation items. A
     * retired or foreign value (a legacy lamp type, READI free text) shows as an
     * extra selected button while it IS the value; picking another drops it.
     */
    function renderButtonSelect(field: SteckbriefVariantField) {
        const rawValue = values[field.key];
        const value = rawValue == null ? null : String(rawValue);
        const options = field.options ?? [];
        const offered = value !== null && options.some((o) => o.value === value);
        const retiredOption = (field.retiredOptions ?? []).find((o) => o.value === value);
        const extraLabel = value === null || offered ? null : retiredOption ? t(retiredOption.labelKey) : value;
        const toggleValue = value ?? (nullIsUnknown(field) ? UNKNOWN_BUTTON_VALUE : null);
        const error = errors[field.key];
        return (
            <FormControl key={field.key} size="small" error={!!error} data-testid={`button-select-${field.key}`}>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                    <FormLabel sx={{ typography: 'body2' }}>{t(field.labelKey)}</FormLabel>
                    <SourceBadge source={sourceOf(field.key)} />
                </Stack>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={toggleValue}
                    onChange={(_, next: string | null) => {
                        if (next === null) return; // "unknown" is an explicit option, never a deselect
                        handleValueChange(field.key, next === UNKNOWN_BUTTON_VALUE ? null : next);
                    }}
                    aria-label={t(field.labelKey)}
                    sx={{ mt: 0.5, flexWrap: 'wrap' }}
                >
                    {options.map((option) => (
                        <ToggleButton key={option.value} value={option.value}>
                            {t(option.labelKey)}
                        </ToggleButton>
                    ))}
                    {extraLabel !== null && value !== null && (
                        <ToggleButton value={value} data-testid={`${field.key}-retired-${value}`}>
                            {extraLabel}
                        </ToggleButton>
                    )}
                    <ToggleButton value={UNKNOWN_BUTTON_VALUE}>{t('unknown')}</ToggleButton>
                </ToggleButtonGroup>
                {(error || field.helpTextKey) && (
                    <FormHelperText error={!!error}>{error ? t(error) : t(field.helpTextKey as string)}</FormHelperText>
                )}
            </FormControl>
        );
    }

    function renderSelect(field: SteckbriefVariantField) {
        const rawValue = values[field.key];
        const value = typeof rawValue === 'string' ? rawValue : rawValue == null ? '' : String(rawValue);
        const options = field.options ?? [];
        // GEN-788: a RETIRED code (the carriers HEAT_PUMP / OTHER of a legacy
        // stamp) appears solely while it IS the value — localized, under "no
        // longer offered"; untouched it saves back verbatim, choosing another
        // option drops it from the menu for good (the GEN-780 heating pattern).
        const retiredOption = (field.retiredOptions ?? []).find((o) => o.value === value);
        // A prefilled value outside the option list (e.g. READI free text) stays selectable verbatim.
        const extraOption = value && !options.some((o) => o.value === value) && !retiredOption ? value : null;
        return (
            <TextField
                key={field.key}
                select
                size="small"
                label={t(field.labelKey)}
                value={value}
                onChange={(e) => handleValueChange(field.key, e.target.value === '' ? null : e.target.value)}
                error={!!errors[field.key]}
                helperText={
                    errors[field.key] ? t(errors[field.key]) : field.helpTextKey ? t(field.helpTextKey) : undefined
                }
            >
                <MenuItem value="">
                    <em>{t('unknown')}</em>
                </MenuItem>
                {extraOption && <MenuItem value={extraOption}>{extraOption}</MenuItem>}
                {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {t(option.labelKey)}
                    </MenuItem>
                ))}
                {retiredOption && <ListSubheader>{t('No longer offered')}</ListSubheader>}
                {retiredOption && (
                    <MenuItem value={retiredOption.value} data-testid={`${field.key}-retired-${retiredOption.value}`}>
                        {t(retiredOption.labelKey)}
                    </MenuItem>
                )}
            </TextField>
        );
    }

    function renderMultiSelect(field: SteckbriefVariantField) {
        const rawValue = values[field.key];
        const value: string[] = Array.isArray(rawValue) ? rawValue.map(String) : [];
        const options = field.options ?? [];
        // Retired options (GEN-701, e.g. the renewables HEAT_PUMP code in a
        // prefilled/carried-forward variant) render like the other non-offered
        // extra values — a deselectable chip, gone from the menu once removed —
        // but keep their localized label instead of showing the raw code.
        const retiredOptions = field.retiredOptions ?? [];
        const extraOptions = value.filter((v) => !options.some((o) => o.value === v));
        const labelKeyFor = (v: string): string | null =>
            (options.find((o) => o.value === v) ?? retiredOptions.find((o) => o.value === v))?.labelKey ?? null;
        const labelFor = (v: string) => {
            const labelKey = labelKeyFor(v);
            return labelKey === null ? v : t(labelKey);
        };
        return (
            <FormControl key={field.key} size="small" error={!!errors[field.key]}>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                    <FormLabel sx={{ typography: 'body2' }}>{t(field.labelKey)}</FormLabel>
                    <SourceBadge source={sourceOf(field.key)} />
                </Stack>
                <Select
                    multiple
                    size="small"
                    value={value}
                    onChange={(e) => {
                        // Defensive: MUI types the multiple-Select value loosely — tolerate
                        // a non-array/non-string value instead of throwing on `.length`.
                        const val = e.target.value;
                        const next = Array.isArray(val) ? val : typeof val === 'string' ? val.split(',') : [];
                        // Re-selected back to the prefilled set (in another click
                        // order): store the prefilled list itself, so the save
                        // payload matches the baseline element-wise like the
                        // backend stamp diff compares it — the badge and the
                        // stamped source both return to the original source.
                        const baseline = prefillFields[field.key]?.value;
                        const reverted = Array.isArray(baseline) && valuesEqual(next, baseline);
                        handleValueChange(field.key, next.length === 0 ? null : reverted ? baseline : next);
                    }}
                    inputProps={{ 'aria-label': t(field.labelKey) }}
                    renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((v) => (
                                <Chip key={v} size="small" label={labelFor(v)} />
                            ))}
                        </Box>
                    )}
                    sx={{ mt: 0.5 }}
                >
                    {[...extraOptions.map((v) => ({ value: v, labelKey: labelKeyFor(v) ?? v })), ...options].map(
                        (option) => (
                            <MenuItem key={option.value} value={option.value}>
                                <Checkbox size="small" checked={value.includes(option.value)} />
                                <ListItemText
                                    primary={option.labelKey === option.value ? option.value : t(option.labelKey)}
                                />
                            </MenuItem>
                        ),
                    )}
                </Select>
                {errors[field.key] && <FormHelperText>{t(errors[field.key])}</FormHelperText>}
            </FormControl>
        );
    }

    function renderBuildingFunction(field: SteckbriefVariantField) {
        const rawValue = values[field.key];
        const value = typeof rawValue === 'string' ? rawValue : '';
        // The fixed Steckbrief list (same set as the PV Analysis tool + Schule
        // mit Sporthalle). A prefill outside it never arrives here —
        // normalizeVariantPrefill drops it, so ops pick.
        return (
            <TextField
                key={field.key}
                select
                size="small"
                label={t(field.labelKey)}
                value={value}
                onChange={(e) => handleValueChange(field.key, e.target.value === '' ? null : e.target.value)}
                error={!!errors[field.key]}
                helperText={errors[field.key] ? t(errors[field.key]) : undefined}
            >
                <MenuItem value="">
                    <em>{t('unknown')}</em>
                </MenuItem>
                {BUILDING_FUNCTION_OPTIONS.map((option) => (
                    <MenuItem key={option.code} value={option.code}>
                        {option.name}
                    </MenuItem>
                ))}
            </TextField>
        );
    }

    function renderNumber(field: SteckbriefVariantField) {
        const rawValue = values[field.key];
        const value = typeof rawValue === 'number' ? rawValue : rawValue == null ? '' : String(rawValue);
        const error = errors[field.key];
        return (
            <TextField
                key={field.key}
                type="number"
                size="small"
                label={t(field.labelKey)}
                value={value}
                onChange={(e) => {
                    const text = e.target.value;
                    handleValueChange(field.key, text === '' ? null : Number(text));
                }}
                error={!!error}
                // Errors go through t() like the GEN-744 controls: exported
                // message keys (e.g. the GEN-774 share bounds) translate, yup's
                // built-in messages pass through unchanged.
                helperText={error ? t(error) : field.helpTextKey ? t(field.helpTextKey) : undefined}
            />
        );
    }

    /**
     * A decimal NUMBER field (GEN-788, the tariffs): German comma or point, the
     * EVEBI default of the latest run as the placeholder while empty. The
     * default is never written into the field — an empty tariff saves empty.
     */
    function renderDecimal(field: SteckbriefVariantField) {
        const error = errors[field.key];
        return (
            <DecimalNumberField
                key={field.key}
                label={t(field.labelKey)}
                value={values[field.key]}
                language={i18n.language}
                onChange={(next) => handleValueChange(field.key, next)}
                error={!!error}
                helperText={error ? t(error) : field.helpTextKey ? t(field.helpTextKey) : undefined}
                placeholder={energyPricePlaceholder(field.key)}
            />
        );
    }

    /**
     * Year-tagged consumption pair (GEN-744): per carrier ONE compact control —
     * the kWh value input plus the year it was measured in. The pair writes
     * `[{ year, consumptionKwh }]` into the values map (a LIST, byte-identical
     * with the backend shape so multiple years can follow), or null while both
     * sub-inputs are empty. Editing either sub-input goes through
     * handleValueChange, so the source badge flips to USER_PROVIDED and the
     * debounced /calculate re-run fires like for every other field. A
     * half-filled pair stays in form state but is blocked at save by the
     * field-model schema (year 1900..current, value > 0, pair complete).
     */
    function renderConsumptionEntries(field: SteckbriefVariantField) {
        const rawValue = values[field.key];
        // The form edits ONE entry for now — index 0 of the (future-multiple) list.
        const entry = Array.isArray(rawValue) ? ((rawValue[0] ?? null) as Partial<ConsumptionEntry> | null) : null;
        // A number, or typed text that is no number (kept so the save is blocked).
        const kwh =
            typeof entry?.consumptionKwh === 'number' || typeof entry?.consumptionKwh === 'string'
                ? entry.consumptionKwh
                : null;
        const year = typeof entry?.year === 'number' ? entry.year : null;
        const error = errors[field.key];

        const write = (nextKwh: number | string | null, nextYear: number | null) => {
            const next: ConsumptionEntry[] | null =
                nextKwh === null && nextYear === null ? null : [{ year: nextYear, consumptionKwh: nextKwh }];
            handleValueChange(field.key, next);
        };

        return (
            <FormControl
                key={field.key}
                size="small"
                error={!!error}
                fullWidth
                data-testid={`consumption-entry-${field.key}`}
            >
                <Stack direction="row" spacing={1}>
                    {/* German thousands points ("7.842" = 7 842 kWh): the number input
                        read them as a decimal point (SB-010595987008-01). */}
                    <DecimalNumberField
                        label={t(field.labelKey)}
                        value={kwh}
                        language={i18n.language}
                        parse={parseGroupedDecimalInput}
                        format={formatGroupedDecimalInput}
                        onChange={(next) => write(next as number | string | null, year)}
                        error={!!error}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        type="number"
                        size="small"
                        label={t('Year')}
                        value={year ?? ''}
                        onChange={(e) => write(kwh, e.target.value === '' ? null : Number(e.target.value))}
                        error={!!error}
                        sx={{ width: 110, flexShrink: 0 }}
                    />
                </Stack>
                {error && <FormHelperText>{t(error)}</FormHelperText>}
            </FormControl>
        );
    }

    function renderText(field: SteckbriefVariantField) {
        const rawValue = values[field.key];
        const value = rawValue == null ? '' : String(rawValue);
        // Character-limited fields (GEN-745 notes, GEN-775 print limits): the
        // input accepts ANY length (QA feedback round — no native clipping of
        // typed or pasted text); the helper text carries a live "n/max"
        // counter next to the help text and, over the limit, the error
        // "Character limit exceeded - 103/60" — the values schema blocks the
        // save meanwhile. A blank input already saves as null via the shared
        // onChange ''→null mapping.
        const counter = field.maxLength === undefined ? null : `${value.length}/${field.maxLength}`;
        const helpText = field.helpTextKey ? t(field.helpTextKey) : null;
        const overLimit = exceedsMaxLength(field, value);
        const hint = counter === null ? (helpText ?? undefined) : [helpText, counter].filter(Boolean).join(' — ');
        // The save-time limit error is shown live instead (and gone once shortened).
        const error = errors[field.key] === CHARACTER_LIMIT_EXCEEDED_MSG ? undefined : errors[field.key];
        const helperText = overLimit
            ? t(CHARACTER_LIMIT_EXCEEDED_MSG, { length: value.length, max: field.maxLength })
            : error
              ? t(error)
              : hint;
        const input = (
            <TextField
                key={field.key}
                size="small"
                multiline={field.type === 'MULTILINE_TEXT'}
                minRows={field.type === 'MULTILINE_TEXT' ? 3 : undefined}
                label={t(field.labelKey)}
                value={value}
                onChange={(e) => handleValueChange(field.key, e.target.value === '' ? null : e.target.value)}
                error={overLimit || !!error}
                helperText={helperText}
                slotProps={{
                    htmlInput: { 'data-testid': `text-${field.key}` },
                    // A multi-line help text (the admi information) keeps its breaks.
                    formHelperText: { sx: { whiteSpace: 'pre-line' } },
                }}
                fullWidth={field.type === 'MULTILINE_TEXT'}
            />
        );
        if (field.key !== 'extraInformation') return input;
        const suggestions = extraInformationSuggestions(value);
        return (
            <Stack key={field.key} spacing={0.5}>
                {input}
                {suggestions.length > 0 && (
                    <Stack spacing={0.25} sx={{ alignItems: 'flex-start' }} data-testid="extra-information-suggestions">
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {t('Suggested for this building:')}
                        </Typography>
                        {suggestions.map((sentence) => (
                            <Button
                                key={sentence}
                                size="small"
                                startIcon={<Add />}
                                onClick={() =>
                                    handleValueChange(
                                        field.key,
                                        value.trim() === '' ? sentence : `${value.trimEnd()} ${sentence}`,
                                    )
                                }
                                sx={{ textTransform: 'none', textAlign: 'left' }}
                            >
                                {sentence}
                            </Button>
                        ))}
                    </Stack>
                )}
            </Stack>
        );
    }

    /**
     * The standard sentences the GEN-801 sheet asks the admi information to
     * include, under their conditions — offered for a one-click insert until the
     * text contains them: the Denkmalschutz sentence while the building is
     * protected, and the heating renewal year while the heating system is younger
     * than 20 years and the heat pump is among the selected measures (the
     * displayed result's selection).
     */
    function extraInformationSuggestions(text: string): string[] {
        const suggestions: string[] = [];
        if (values['monumentProtection'] === true) suggestions.push(EXTRA_INFORMATION_MONUMENT_SENTENCE);
        // Only a year the advisor declared known (QA feedback round gate) — a
        // hidden stale year is nulled in every payload.
        const heatingYear = values['heatingConstructionYearKnown'] === false ? null : values['heatingConstructionYear'];
        if (
            typeof heatingYear === 'number' &&
            new Date().getFullYear() - heatingYear < HEATING_RENEWAL_AGE_YEARS &&
            effectiveSelected.includes('HEATING_RENEWAL')
        ) {
            suggestions.push(heatingRenewalSentence(heatingYear + HEATING_RENEWAL_AGE_YEARS));
        }
        return suggestions.filter((sentence) => !text.includes(sentence));
    }

    function renderPhotoField(field: SteckbriefVariantField) {
        const value = values[field.key];
        // A data URI is a photo processed by the capture pipeline (previewable);
        // any other non-null value is an opaque reference carried over from a
        // previous Variant — passed through verbatim on save so it is not lost.
        const isDataUri = typeof value === 'string' && value.startsWith('data:image');
        const hasCarriedPhoto = value != null && !isDataUri;
        return (
            <Stack key={field.key} spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                    <Typography variant="body2">{t(field.labelKey)}</Typography>
                    <SourceBadge source={sourceOf(field.key)} />
                </Stack>
                {isDataUri && (
                    <Box
                        component="img"
                        src={value}
                        alt={t(field.labelKey)}
                        sx={{ maxWidth: 290, width: '100%', borderRadius: 1, border: 1, borderColor: 'divider' }}
                    />
                )}
                {value != null && (
                    <Button
                        size="small"
                        color="inherit"
                        startIcon={<Delete />}
                        onClick={() => handleValueChange(field.key, null)}
                    >
                        {t('Remove photo')}
                    </Button>
                )}
                {hasCarriedPhoto && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {t('A photo from the previous variant is carried over.')}
                    </Typography>
                )}
                {/* GEN-778: no upload — the photo is taken on the building map above. */}
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t(
                        'Frame the building on the map above and click "Save map image" — the capture is masked to the print layout automatically.',
                    )}
                </Typography>
            </Stack>
        );
    }

    /**
     * Month picker (GEN-786, the Stand): year + month views only, displayed like
     * the print ("September 2026"); the value is the "YYYY-MM" string. Every edit
     * reaches the form state: a cleared field saves null (the print then falls
     * back to the generation month), a half-typed/invalid entry is stored as an
     * invalid marker so the YYYY-MM schema blocks the save with its inline error —
     * and the invalid Dayjs is handed back to the picker, keeping the typed text
     * visible for correction.
     */
    function renderMonth(field: SteckbriefVariantField) {
        const rawValue = values[field.key];
        const month = typeof rawValue === 'string' ? dayjs(`${rawValue}-01`) : null;
        const error = errors[field.key];
        return (
            <LocalizationProvider
                key={field.key}
                dateAdapter={AdapterDayjs}
                adapterLocale={i18n.language.startsWith('de') ? 'de' : 'en'}
            >
                <DatePicker
                    label={t(field.labelKey)}
                    views={['year', 'month']}
                    openTo="month"
                    format="MMMM YYYY"
                    value={month}
                    onChange={(next) => {
                        if (next === null) handleValueChange(field.key, null);
                        else handleValueChange(field.key, next.isValid() ? next.format('YYYY-MM') : INVALID_MONTH);
                    }}
                    slotProps={{
                        textField: {
                            size: 'small',
                            error: !!error,
                            helperText: error ? t(error) : field.helpTextKey ? t(field.helpTextKey) : undefined,
                        },
                    }}
                />
            </LocalizationProvider>
        );
    }

    function renderField(field: SteckbriefVariantField) {
        if (!fieldVisible(field)) return null;
        // GEN-798 follow-up: shown in "Assumptions & derived values" only.
        if (field.displayOnly) return null;

        if (field.type === 'MONTH') return renderMonth(field);
        if (field.type === 'TRI_STATE') return renderTriState(field);
        if (field.type === 'ATTACHMENT') return renderPhotoField(field);
        if (field.type === 'MULTI_SELECT') return renderMultiSelect(field);
        if (field.type === 'CONSUMPTION_ENTRIES') return renderConsumptionEntries(field);
        if (field.key === 'buildingFunction') return renderBuildingFunction(field);
        if (field.type === 'SELECT' && field.buttons) return renderButtonSelect(field);
        if (field.type === 'SELECT') return renderSelect(field);
        if (field.decimalInput) return renderDecimal(field);
        if (field.type === 'NUMBER' || field.type === 'YEAR') return renderNumber(field);
        return renderText(field);
    }

    /**
     * Tooltip of a "Default" badge (GEN-802): the Gebäudetyp's own
     * default, or the general one when the type is missing or has none.
     */
    function assumptionBadgeTitle(key: string): string | undefined {
        if (!isAssumptionDefaultedField(key)) return undefined;
        const assumptionDefault = resolveAssumptionDefault(assumptionConfig, key, values['buildingFunction']);
        if (assumptionDefault === null) return undefined;
        // A BUILDING_FUNCTION level implies a listed code, hence a string.
        const code = String(values['buildingFunction']);
        return assumptionDefault.level === 'BUILDING_FUNCTION'
            ? t('Default for {{buildingFunction}} — follows the building type until you change it.', {
                  buildingFunction: BUILDING_FUNCTION_NAMES[code] ?? code,
              })
            : t(
                  'General default (building type missing or without its own default) — follows the building type until you change it.',
              );
    }

    /** Badge row for TextField-based controls (their label lives inside the input). */
    function withBadge(field: SteckbriefVariantField, control: ReactNode) {
        if (control === null) return null;
        // MONTH (GEN-786, the Stand) is a document setting, not building data —
        // a provenance badge would only ever say "User provided" there.
        const badgeless =
            field.type === 'TRI_STATE' ||
            field.type === 'ATTACHMENT' ||
            field.type === 'MULTI_SELECT' ||
            field.type === 'MONTH' ||
            field.buttons === true;
        return (
            <Box
                key={field.key}
                // The scroll target of the first invalid field (validateValues).
                data-field-key={field.key}
                // Full-width multiline texts; GEN-786 `startsRow` pins a field to
                // the left column of a new row (a single-column grid ignores it).
                sx={
                    field.type === 'MULTILINE_TEXT'
                        ? { gridColumn: '1 / -1' }
                        : field.startsRow
                          ? { gridColumnStart: 1 }
                          : undefined
                }
            >
                <Stack spacing={0.25}>
                    {control}
                    {!badgeless && (
                        <Box sx={{ minHeight: 20 }}>
                            <SourceBadge source={sourceOf(field.key)} title={assumptionBadgeTitle(field.key)} />
                        </Box>
                    )}
                </Stack>
            </Box>
        );
    }

    /**
     * One print section's field grid. The heat consumption gets the
     * consumption explanation beside it (QA feedback round): the two
     * consumption rows keep their right column free (GEN-786 `startsRow`), so
     * the note spans both rows there.
     */
    function renderSection(section: string, labelKey: string) {
        const sectionFields = STECKBRIEF_VARIANT_FIELDS.filter((field) => field.section === section);
        const rendered = sectionFields.flatMap((field) => {
            const node = withBadge(field, renderField(field));
            if (node === null) return [];
            if (field.key !== 'heatConsumption') return [node];
            return [
                node,
                <Typography
                    key="consumption-explanation"
                    variant="body2"
                    data-testid="consumption-explanation"
                    sx={{ color: 'text.secondary', gridColumn: { sm: '2' }, gridRow: { sm: 'span 2' } }}
                >
                    {t(
                        'Specify the yearly heat and electricity consumption and the years they are from. This should be the consumption of all the buildings you have selected for this Steckbrief.',
                    )}
                </Typography>,
            ];
        });
        if (rendered.length === 0) return null;
        return (
            <Box key={section} data-testid={`variant-form-section-${section}`}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t(labelKey)}
                </Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 1.5,
                    }}
                >
                    {rendered}
                </Box>
            </Box>
        );
    }

    /**
     * The CityGML-derived building figures without a value (QA feedback
     * round) — judged on the settled prefill only, never while it re-resolves.
     */
    const missingCityGmlKeys = prefillLoading ? [] : missingCityGmlFieldKeys(values);
    const photoSection = VARIANT_FORM_SECTIONS.find((entry) => entry.section === 'BUILDING_PHOTO');

    /**
     * One in-flight page action at a time — every footer action disables the
     * others. The preview renders in the background — it never blocks the
     * actions. The PV refresh (GEN-799) counts: it ends with a recalculation
     * whose result the save must stamp, not the one displayed before.
     */
    const busy = saving || generating || pvRefreshing;
    /**
     * The save-shaped actions wait for an outdated result — and for a map
     * subset refresh (`prefillLoading`): until its prefill merged, the values
     * belong to the previous building subset while the server re-resolves the
     * save from the new one, so untouched fields would stamp USER_PROVIDED.
     */
    const saveBlocked = busy || calcOutdated || prefillLoading;
    // Shown until every inline error of the refused save is fixed — each edit clears its own.
    const saveErrorsShown =
        saveRefused &&
        (nameError !== null || Object.keys(errors).length > 0 || Object.keys(measureReasonErrors).length > 0);

    /** Brings the first marked field into view again — name, then the values, then the measure reasons. */
    function scrollToFirstError() {
        const firstField = STECKBRIEF_VARIANT_FIELDS.find((field) => errors[field.key] !== undefined);
        const firstReason = Object.keys(measureReasonErrors)[0];
        const target = nameError
            ? document.getElementById(VARIANT_NAME_INPUT_ID)
            : firstField
              ? document.querySelector(`[data-field-key="${firstField.key}"]`)
              : firstReason
                ? document.getElementById(`measure-reason-${firstReason}`)
                : null;
        target?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }

    return (
        <Stack spacing={2}>
            {/* The Gebäudefoto first (QA feedback round): the page's building map
                sits directly above the form and takes the photo, so the capture
                shows right under it. */}
            {photoSection && renderSection(photoSection.section, photoSection.labelKey)}

            {/* Missing building geometry (QA feedback round): the surfaces feed
                the item-by-item investment estimate, so say which are missing. */}
            {missingCityGmlKeys.length > 0 && (
                <Alert severity="warning" data-testid="citygml-missing-warning">
                    {t(
                        'Building geometry (CityGML) values are not available: {{fields}}. The investment estimates that depend on them cannot be computed or are incomplete.',
                        {
                            fields: missingCityGmlKeys
                                .map((key) => t(variantFieldByKey(key)?.labelKey ?? key))
                                .join(', '),
                        },
                    )}
                </Alert>
            )}

            {/* Variant name — a plain form field since the pre-step page was
                removed (GEN-701 QA feedback), mirroring PV Analysis' inline
                "Variation Name" field. Open mode (GEN-765): seeded EMPTY with
                the source Variant's name as placeholder — saving always creates
                a NEW Variant. Uniqueness among active names is checked live. */}
            <TextField
                required
                size="small"
                autoFocus
                id={VARIANT_NAME_INPUT_ID}
                label={t('Variant name')}
                value={name}
                placeholder={openedVariant?.name}
                onChange={(e) => {
                    setName(e.target.value);
                    const trimmed = e.target.value.trim();
                    setNameError(trimmed !== '' && isNameTaken(trimmed) ? t(NAME_TAKEN_MSG_KEY) : null);
                }}
                error={!!nameError}
                helperText={nameError ?? (openedVariant ? t('Saving creates a new variant.') : undefined)}
                sx={{ maxWidth: 480 }}
            />

            {/* Kind switch (field mapping #49) — drives the kind-conditional blocks. */}
            <FormControl>
                <FormLabel sx={{ typography: 'body2' }}>{t('Variant kind')}</FormLabel>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={kind}
                    onChange={(_, next: SteckbriefKind | null) => {
                        if (next === null) return;
                        // GEN-806: the calculation depends on the kind (SALES estimates a
                        // missing NGF/BGF from the building model). A current result follows
                        // the switch at once — and the preview with it; an outdated one is
                        // recalculated for the new kind by the next "(Re-)calculate".
                        const wasCurrent = calcInputs !== null && !calcOutdated;
                        kindRef.current = next;
                        setKind(next);
                        if (wasCurrent) {
                            runCalculate(
                                valuesRef.current,
                                selectedMeasuresRef.current,
                                heatingOverrideRef.current,
                                priorityOverrideRef.current,
                            );
                        }
                    }}
                    aria-label={t('Variant kind')}
                    sx={{ mt: 0.5 }}
                >
                    <ToggleButton value="SALES">SALES</ToggleButton>
                    <ToggleButton value="FINAL">FINAL</ToggleButton>
                </ToggleButtonGroup>
            </FormControl>

            {VARIANT_FORM_SECTIONS.filter(({ section }) => section !== 'BUILDING_PHOTO').map(({ section, labelKey }) =>
                renderSection(section, labelKey),
            )}

            <Divider />

            {/* On-demand calculation (like PV Analysis' "(Re-)calculate"): enabled
                once the inputs drifted from the displayed result — or to retry a
                failed run. */}
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<Refresh />}
                    onClick={handleRecalculate}
                    disabled={calcLoading || pvRefreshing || !(calcOutdated || calcError !== null)}
                    data-testid="recalculate"
                >
                    {t('(Re-)calculate')}
                </Button>
                {calcBlocked && Object.keys(errors).length > 0 ? (
                    <Typography variant="body2" sx={{ color: 'error.main' }} data-testid="calc-blocked-hint">
                        {t('Complete the marked fields to calculate.')}
                    </Typography>
                ) : (
                    calcOutdated && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testid="calc-outdated-hint">
                            {t('The data has changed — recalculate to update the measures and the prognosis.')}
                        </Typography>
                    )
                )}
            </Stack>
            {/* GEN-799: the refresh request itself failed — the displayed line stays. */}
            {pvRefreshError && (
                <Alert severity="warning" onClose={() => setPvRefreshError(null)} data-testid="pv-refresh-error">
                    {pvRefreshError}
                </Alert>
            )}

            {/* The calculated result — outlined orange while outdated, like the
                PV Analysis result box. */}
            <Stack
                spacing={2}
                data-testid="calc-result"
                data-outdated={calcOutdated}
                sx={{
                    p: '3px',
                    borderRadius: 1,
                    border: '2px solid',
                    borderColor: calcOutdated ? 'orange' : 'transparent',
                }}
            >
                {/* Maßnahmen (field mapping #37–#39): proposal chips + advisor selection. */}
                <Box data-testid="variant-form-measures-panel">
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">{t('Measures')}</Typography>
                        {calcLoading && <CircularProgress size={14} />}
                        {calc && (
                            <>
                                <Box sx={{ flexGrow: 1 }} />
                                {/* GEN-755: one action back to the system proposal —
                                every row's classification and the heating system
                                at once. Disabled while the panel already matches. */}
                                <Button
                                    size="small"
                                    startIcon={<RestartAlt />}
                                    onClick={handleResetToRecommendation}
                                    disabled={panelAtDefaults}
                                    data-testid="reset-to-recommendation"
                                >
                                    {t('Reset to system proposal')}
                                </Button>
                            </>
                        )}
                    </Stack>
                    {calcError && (
                        <Alert severity="warning" sx={{ mb: 1 }}>
                            {calcError}
                        </Alert>
                    )}
                    {calc === null && !calcError ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('Calculating the measure proposal…')}
                        </Typography>
                    ) : (
                        calc && (
                            <Stack spacing={2}>
                                {/* GEN-786: no heating-system control any more — the heating
                                measure always proposes the heat pump (GEN-780), so "Proposed
                                heating system (after renovation)" asked nothing. A legacy
                                stamp's override still replays verbatim (heatingOverride),
                                and the reset button clears it. */}
                                <MeasuresReviewList
                                    rows={measureRowViews}
                                    result={calc}
                                    reasons={measureReasons}
                                    reasonErrors={measureReasonErrors}
                                    unavailable={unavailable}
                                    onClassificationChange={handleClassificationChange}
                                    onReasonChange={handleMeasureReasonChange}
                                />
                            </Stack>
                        )
                    )}
                </Box>

                {/* GEN-700 D4: lookalike decision support in its own block — search comparable
                buildings and read the Gebäudevergleich to inform the forecast adjustments below. */}
                {calc && (
                    <Box
                        data-testid="variant-form-lookalike-block"
                        sx={{ p: 1.5, borderRadius: 1, border: 1, borderColor: 'divider' }}
                    >
                        <SteckbriefLookalikePanel
                            steckbriefId={steckbrief.id}
                            request={{
                                values: calculationValues(values),
                                selectedMeasures,
                                heatingSystemOverride: heatingOverride,
                                priorityOverride,
                                pvEstimateId: pvEstimateIdRef.current,
                                kind: kindRef.current,
                            }}
                            subjectName={steckbrief.name}
                            subjectSubtitle={steckbrief.code}
                            investmentCostPerSqmRange={calc.investmentCostPerSqmRange}
                            investmentCostDerivedTotalRange={calc.investmentCostDerivedTotalRange}
                            onReferenceContext={handleLookalikeReference}
                            stampedComparisonValue={values[LOOKALIKE_REFERENCE_KEYS.comparison]}
                            referenceStale={calc.lookalikeReferenceStale}
                        />
                    </Box>
                )}

                {/* KPI preview (Ersteinschätzung) — recomputed on every /calculate run. */}
                {calc && (
                    <Box data-testid="kpi-preview" sx={{ p: 1.5, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                        <Stack
                            direction="row"
                            spacing={1}
                            useFlexGap
                            sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }}
                        >
                            <Typography variant="subtitle2">{t('Initial assessment & forecast')}</Typography>
                            {/* GEN-788: how many prognosis warnings this run raised — warning
                                colour as soon as one questions a number, info otherwise. */}
                            {warningSummary.count > 0 && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    color={warningSummary.hasWarning ? 'warning' : 'info'}
                                    label={t('{{count}} prognosis warnings', { count: warningSummary.count })}
                                    data-testid="kpi-prognosis-warning-count"
                                />
                            )}
                        </Stack>
                        <Stack spacing={1}>
                            <Stack
                                direction="row"
                                spacing={1}
                                useFlexGap
                                sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                            >
                                <Chip
                                    size="small"
                                    label={`${t('Current class')}: ${calc.orientationClass ?? '–'}`}
                                    data-testid="kpi-orientation-class"
                                />
                                <Chip
                                    size="small"
                                    label={`${t('Target class')}: ${calc.targetClass ?? '–'}`}
                                    color="success"
                                    variant="outlined"
                                    data-testid="kpi-target-class"
                                />
                                {calc.renovationNeedBadge && (
                                    <Chip size="small" color="warning" label={calc.renovationNeedBadge} />
                                )}
                            </Stack>
                            <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {t('Orientation value (heat + electricity per NGF)')}:{' '}
                                    {formatNumber(calc.orientationValue)} kWh/(m²a)
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(100, Math.max(0, calc.gaugePointerPct ?? 0))}
                                    sx={{ mt: 0.5 }}
                                />
                            </Box>
                            {/* GEN-788: the prognosis model's warnings, read before the numbers. */}
                            <SteckbriefPrognosisWarnings warnings={calc.prognosisWarnings} />
                            {/* GEN-782: Systemprognose + adjustable Prognosewerte. */}
                            <SteckbriefPrognosisPanel
                                calc={calc}
                                values={values}
                                errors={errors}
                                onApply={handleApplyValues}
                            />
                            {/* GEN-780: the two printed investment totals (all selected
                            measures · the effective Prio set) — raw EUR from the
                            engine, locale-formatted here (ADR-018); "k. A." like the
                            print when the engine gives none. */}
                            <Box data-testid="kpi-investment">
                                {/* QA feedback round: the investment cost factor scales
                                every estimate below — edited right here, applied on
                                "(Re-)calculate" like every input. */}
                                <Box sx={{ maxWidth: 360, mb: 1 }}>
                                    {STECKBRIEF_VARIANT_FIELDS.filter((field) => field.section === 'INVESTMENT').map(
                                        (field) => withBadge(field, renderField(field)),
                                    )}
                                </Box>
                                <Typography variant="body2" data-testid="kpi-investment-total">
                                    {t('Investment costs (complete renovation)')}:{' '}
                                    {formatEuroRange(i18n.language, calc.investmentCostRange) ??
                                        t(INVESTMENT_NOT_AVAILABLE_KEY)}
                                </Typography>
                                <Typography variant="body2" data-testid="kpi-investment-priority">
                                    {t('Investment costs (priority measures)')}:{' '}
                                    {formatEuroRange(i18n.language, calc.priorityInvestmentCostRange) ??
                                        t(INVESTMENT_NOT_AVAILABLE_KEY)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {t(
                                        'Rounded to the nearest 100,000 € — itemised per measure under "Assumptions & derived values".',
                                    )}
                                </Typography>
                            </Box>
                            {/* No engine funding output (GEN-779): the print shows the
                            manual Förderung values of the form — neither the engine's
                            rate, its German Hinweis (fundingNote) nor its advisor
                            notes are shown, so the form never contradicts the PDF. */}
                        </Stack>
                    </Box>
                )}

                {/* Inline PDF preview: the real /document-preview render, between
                the Ersteinschätzung and the read-only assumptions. */}
                {onPreview && (
                    <SteckbriefPdfPreviewPanel
                        preview={preview}
                        fallbackFileName={previewFileName}
                        loading={previewing}
                        error={previewError}
                        outdated={calcOutdated}
                        incomplete={!valuesComplete}
                    />
                )}

                {/* Annahmen & abgeleitete Werte (GEN-756): read-only ops visibility
                under the form — fired assumptions + derived outputs of the
                LATEST /calculate run. Display-only: never stamped or printed. */}
                <SteckbriefAssumptionsSection
                    calc={calc}
                    loading={calcLoading}
                    enteredEnergyPrices={enteredEnergyPrices}
                    // GEN-799: the refresh acts on the CURRENT values, so it is
                    // offered only while they still match the displayed run.
                    onRefreshPvEstimate={calcOutdated ? undefined : () => void handleRefreshPvEstimate()}
                    pvEstimateRefreshing={pvRefreshing}
                    inputValues={calcInputs?.values ?? null}
                />
            </Stack>

            <Divider />

            {/* Footer actions (GEN-765): the plain save, save-then-generate — and, on an opened Variant
                without a document, generation from its SAVED stamp. */}
            <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}
            >
                {calcOutdated && (
                    <Typography variant="caption" sx={{ color: 'warning.main' }} data-testid="save-blocked-hint">
                        {t('Recalculate before saving or downloading the preview.')}
                    </Typography>
                )}
                {saveErrorsShown && (
                    <Typography variant="body2" sx={{ color: 'error.main' }} data-testid="save-errors-hint">
                        {t('Not saved — complete the fields marked in red.')}{' '}
                        <Link component="button" type="button" color="error" onClick={scrollToFirstError}>
                            {t('Show')}
                        </Link>
                    </Typography>
                )}
                <Button onClick={onCancel} disabled={busy}>
                    {t('Cancel')}
                </Button>
                {onGenerateOpened && openedVariant && openedVariant.documentInstanceId === null && (
                    <Tooltip
                        title={t(
                            'Generates the PDF from the saved variant — unsaved edits on this page are not included.',
                        )}
                    >
                        {/* span: Tooltip needs an enabled event target even while the button is disabled */}
                        <span>
                            <Button
                                startIcon={generating ? <CircularProgress size={14} /> : <PictureAsPdf />}
                                onClick={onGenerateOpened}
                                disabled={busy}
                                data-testid="generate-opened-variant"
                            >
                                {t('Generate PDF')}
                            </Button>
                        </span>
                    </Tooltip>
                )}
                <Button variant="outlined" onClick={() => void handleSaveAction(onSave)} disabled={saveBlocked}>
                    {t('Save variant')}
                </Button>
                {onSaveAndGenerate && (
                    <Button
                        variant="contained"
                        onClick={() => void handleSaveAction(onSaveAndGenerate)}
                        disabled={saveBlocked}
                        data-testid="save-and-generate"
                    >
                        {t('Save & generate PDF')}
                    </Button>
                )}
            </Stack>
        </Stack>
    );
}
